import React, { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, PieChart, BarChart3, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';


// ── Data loaded from Excel ──
const MARKET_SCENARIOS = {
  round1: {
    name: 'Weak Market - Rate Uncertainty',
    description: 'Negative equity performance, bonds perform well',
    cash: 0.005
  },
  round2: {
    name: 'High Volatility - Market Correction',
    description: 'Severe equity drawdown, flight to safety in bonds',
    cash: 0.005
  },
  round3: {
    name: 'Strong Recovery - Risk-On Rally',
    description: 'Strong equity rebound, bonds underperform',
    cash: 0.005
  }
};

const MARKET_RETURNS = {
  round1: -0.04,   // -4%
  round2: -0.178,  // -17.8%
  round3: 0.203    // +20.3%
};

const INITIAL_STOCKS = {
  technology: [
    { name: 'Alphabet Inc-A', ticker: 'GOOGL US', allocation: 0.05 },
    { name: 'Apple Inc', ticker: 'AAPL US', allocation: 0.05 },
    { name: 'Amazon.Com Inc', ticker: 'AMZN US', allocation: 0.05 },
    { name: 'Microsoft Corp', ticker: 'MSFT US', allocation: 0.05 },
    { name: 'Meta Platforms-A', ticker: 'META US', allocation: 0.05 },
    { name: 'Nvidia Corp', ticker: 'NVDA US', allocation: 0.05 },
    { name: 'Tesla Inc', ticker: 'TSLA US', allocation: 0.05 },
    { name: 'Micron Tech', ticker: 'MU US', allocation: 0.05 },
    { name: 'Alibaba Group', ticker: '9988 HK', allocation: 0.05 },
    { name: 'Baidu Inc', ticker: '9888 HK', allocation: 0.05 },
    { name: 'Tencent', ticker: '700 HK', allocation: 0.05 },
  ],
  healthcare: [
    { name: 'Amgen Inc', ticker: 'AMGN US', allocation: 0 },
    { name: 'Astrazeneca Plc', ticker: 'AZN LN', allocation: 0 },
    { name: 'Bayer Ag-Reg', ticker: 'BAYN GY', allocation: 0 },
    { name: 'Gsk Plc', ticker: 'GSK LN', allocation: 0 },
    { name: 'Johnson&Johnson', ticker: 'JNJ US', allocation: 0 },
    { name: 'Eli Lilly & Co', ticker: 'LLY US', allocation: 0 },
    { name: 'Novo Nordisk-B', ticker: 'NOVOB DC', allocation: 0 },
    { name: 'Pfizer Inc', ticker: 'PFE US', allocation: 0 },
  ],
  consumer: [
    { name: 'BYD Company', ticker: '1211 HK', allocation: 0 },
    { name: 'Toyota Motor', ticker: '7203 JP', allocation: 0 },
    { name: 'BMW', ticker: 'BMW GY', allocation: 0 },
    { name: 'Mercedes-Benz Gr', ticker: 'MBG GY', allocation: 0 },
    { name: 'Nintendo Co Ltd', ticker: '7974 JP', allocation: 0 },
    { name: 'Adidas', ticker: 'ADS GY', allocation: 0 },
    { name: 'Nike', ticker: 'NKE US', allocation: 0 },
    { name: 'Richemont', ticker: 'CFR SW', allocation: 0 },
    { name: 'LVMH', ticker: 'MC FP', allocation: 0 },
    { name: 'Hermes Intl', ticker: 'RMS FP', allocation: 0 },
    { name: 'McDonalds Corp', ticker: 'MCD US', allocation: 0 },
    { name: 'Starbucks Corp', ticker: 'SBUX US', allocation: 0 },
    { name: 'Carrefour Sa', ticker: 'CA FP', allocation: 0 },
    { name: 'Costco Wholesale', ticker: 'COST US', allocation: 0 },
    { name: 'Walmart Inc', ticker: 'WMT US', allocation: 0 },
    { name: 'Colgate-Palmoliv', ticker: 'CL US', allocation: 0 },
    { name: 'Procter & Gamble', ticker: 'PG US', allocation: 0 },
  ],
  industrials: [
    { name: 'General Electric', ticker: 'GE US', allocation: 0 },
    { name: 'Fedex Corp', ticker: 'FDX US', allocation: 0 },
    { name: 'United Parcel-B', ticker: 'UPS US', allocation: 0 },
    { name: 'Caterpillar Inc', ticker: 'CAT US', allocation: 0 },
    { name: 'ABB Ltd-Reg', ticker: 'ABBN SW', allocation: 0 },
    { name: 'Emerson Elec Co', ticker: 'EMR US', allocation: 0 },
    { name: 'Schneider Electr', ticker: 'SU FP', allocation: 0 },
    { name: 'Hitachi Ltd', ticker: '6501 JP', allocation: 0 },
    { name: 'Honeywell Intl', ticker: 'HON US', allocation: 0 },
    { name: 'Siemens Ag-Reg', ticker: 'SIE GY', allocation: 0 },
  ],
  metalsAndMining: [
    { name: 'Agnico Eagle Min', ticker: 'AEM CN', allocation: 0 },
    { name: 'Newmont Corp', ticker: 'NEM US', allocation: 0 },
    { name: 'Antofagasta Plc', ticker: 'ANTO LN', allocation: 0 },
    { name: 'Holcim Ltd', ticker: 'HOLN SW', allocation: 0 },
    { name: 'Basf Se', ticker: 'BAS GY', allocation: 0 },
    { name: 'Anglo Amer Plc', ticker: 'AAL LN', allocation: 0 },
    { name: 'BHP Group Ltd', ticker: 'BHP AU', allocation: 0 },
    { name: 'Rio Tinto Plc', ticker: 'RIO LN', allocation: 0 },
    { name: 'Air Liquide Sa', ticker: 'AI FP', allocation: 0 },
    { name: 'Linde Plc', ticker: 'LIN US', allocation: 0 },
    { name: 'Dupont De Nemour', ticker: 'DD US', allocation: 0 },
    { name: 'BP Plc', ticker: 'BP/ LN', allocation: 0 },
    { name: 'Chevron Corp', ticker: 'CVX US', allocation: 0 },
    { name: 'Occidental Pete', ticker: 'OXY US', allocation: 0 },
    { name: 'Shell Plc', ticker: 'SHELL NA', allocation: 0 },
    { name: 'Totalenergies Se', ticker: 'TTE FP', allocation: 0 },
    { name: 'Exxon Mobil Corp', ticker: 'XOM US', allocation: 0 },
    { name: 'Halliburton Co', ticker: 'HAL US', allocation: 0 },
    { name: 'SLB Ltd', ticker: 'SLB US', allocation: 0 },
    { name: 'Conocophillips', ticker: 'COP US', allocation: 0 },
  ],
  financials: [
    { name: 'Citigroup Inc', ticker: 'C US', allocation: 0 },
    { name: 'Goldman Sachs Gp', ticker: 'GS US', allocation: 0 },
    { name: 'JPMmorgan Chase', ticker: 'JPM US', allocation: 0 },
    { name: 'DBS Group Hldgs', ticker: 'DBS SP', allocation: 0 },
    { name: 'OCBC Bank', ticker: 'OCBC SP', allocation: 0 },
    { name: 'UOB', ticker: 'UOB SP', allocation: 0 },
    { name: 'BNP Paribas', ticker: 'BNP FP', allocation: 0 },
    { name: 'Deutsche Bank', ticker: 'DBK GY', allocation: 0 },
    { name: 'UBS Group AG', ticker: 'UBSG SW', allocation: 0 },
  ],
  utilities: [
    { name: 'Enel Spa', ticker: 'ENEL IM', allocation: 0 },
    { name: 'Iberdrola Sa', ticker: 'IBE SM', allocation: 0 },
    { name: 'Nextera Energy', ticker: 'NEE US', allocation: 0 },
    { name: 'SSE Plc', ticker: 'SSE LN', allocation: 0 },
    { name: 'RWE AG', ticker: 'RWE GY', allocation: 0 },
    { name: 'Engie', ticker: 'ENGI FP', allocation: 0 },
    { name: 'E.On Se', ticker: 'EOAN GY', allocation: 0 },
    { name: 'Veolia Environ.', ticker: 'VIE FP', allocation: 0 },
  ],
};

const INITIAL_BONDS = {
  treasuries: [
    { name: 'Treasury Bill 0% |2.80%', ticker: 'US Bill 3m', allocation: 0 },
    { name: 'Treasury Bill 0% |2.60%', ticker: 'US Bill 6m', allocation: 0 },
    { name: 'Treasury Bill 0% |3.20%', ticker: 'US Bill 12m', allocation: 0 },
    { name: 'US Treasury N/B 3.625% |3.83%', ticker: 'UST5Y', allocation: 0 },
    { name: 'US Treasury N/B 4% |4.25%', ticker: 'UST10Y', allocation: 0.1 },
    { name: 'US Treasury N/B 4.625% |4.83%', ticker: 'UST20Y', allocation: 0 },
    { name: 'US Treasury N/B 4.625% |4.87%', ticker: 'UST30Y', allocation: 0 },
  ],
  corporate: [
    { name: 'Microsoft Corp 3.5% |4.31%', ticker: 'MSFT35', allocation: 0 },
    { name: 'Tencent Holdings Ltd 2.39% |4.08%', ticker: 'Tencent30', allocation: 0 },
    { name: 'CK Hutchison Intl 23 4.875% |4.67%', ticker: 'CkHut33', allocation: 0 },
    { name: 'Baidu Inc 4.375% |4.02%', ticker: 'BIDU28', allocation: 0 },
    { name: 'Aia Group Ltd 5.375% |4.88%', ticker: 'AIA34', allocation: 0 },
    { name: 'JPMorgan Chase & Co 5.35% |4.77%', ticker: 'JPM34', allocation: 0 },
    { name: 'JD.Com Inc 3.375% |4.19%', ticker: 'JD30', allocation: 0 },
    { name: 'Daimler Truck Finan Na 5.125% |4.11%', ticker: 'DAI28', allocation: 0 },
    { name: 'UBS Group AG 6.246% |4.19%', ticker: 'UBS29', allocation: 0 },
    { name: 'HSBC Holdings Plc 5.741% |5.36%', ticker: 'HSBC36', allocation: 0 },
    { name: 'General Motors Finl Co 5.55% |4.37%', ticker: 'GM29', allocation: 0 },
    { name: 'Societe Generale 6.446% |4.31%', ticker: 'SocGen29', allocation: 0 },
    { name: 'Dell Int Llc / Emc Corp 5.4% |5.02%', ticker: 'DELL34', allocation: 0 },
  ],
};

const STRUCTURED_PRODUCTS = {
  capitalPreservation: [
    {
      ticker: 'China',
      name: '3M MRN Booster on CSI300 + CSI500| 125.36% PR| 90% MRN',
      theme: 'China',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.1,
      round3: 0.06343216,
    },
    {
      ticker: 'Gold',
      name: '6M Bullish Sharkfin on GOLDLNPM| 100% Strike| 120.75% KO| 100%MRN ',
      theme: 'Gold',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.15,
      round2: 0.0831,
      round3: -0.07672421752377434,
    },
    {
      ticker: 'SPX 3m BullShark',
      name: '3M Bullish Sharkfin on SPX| 100% Strike| 115% KO| 97%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.030000000000000027,
      round3: 0.07289999999999996,
    },
    {
      ticker: 'NDX 3m BullShark',
      name: '3M Bullish Sharkfin on NDX| 100% Strike| 150% KO| 95.3%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.04700000000000004,
      round3: 0.3027999999999999,
    },
    {
      ticker: 'SX5E 3m BullShark',
      name: '3M Bullish Sharkfin on SX5E| 100% Strike| 120% KO| 97.45%MRN ',
      theme: 'Europe',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.025499999999999967,
      round3: 0.12900000000000003,
    },
    {
      ticker: 'HSCEI 3m BullShark',
      name: '3M Bullish Sharkfin on HSCEI| 100% Strike| 115% KO| 98.6%MRN ',
      theme: 'China',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.014000000000000012,
      round3: 0.008599999999999986,
    },
    {
      ticker: 'NKY 3m BullShark',
      name: '3M Bullish Sharkfin on NKY| 100% Strike| 120% KO| 98.5%MRN ',
      theme: 'Japan',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.015000000000000013,
      round3: 0.06359999999999999,
    },
    {
      ticker: 'SPX 6m BullShark',
      name: '6M Bullish Sharkfin on SPX| 100% Strike| 115% KO| 97.6%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: -0.02400000000000002,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'NDX 6m BullShark',
      name: '6M Bullish Sharkfin on NDX| 100% Strike| 150% KO| 93.8%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: -0.062000000000000055,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'SX5E 6m BullShark',
      name: '6M Bullish Sharkfin on SX5E| 100% Strike| 120% KO| 97.49%MRN ',
      theme: 'Europe',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: -0.02510000000000001,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'HSCEI 6m BullShark',
      name: '6M Bullish Sharkfin on HSCEI| 100% Strike| 115% KO| 100%MRN ',
      theme: 'China',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'NKY 6m BullShark',
      name: '6M Bullish Sharkfin on NKY| 100% Strike| 115% KO| 100%MRN ',
      theme: 'Japan',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'SPX 3m BearShark',
      name: '3M Bearish Sharkfin on SPX| 100% Strike| 70% KO| 98.3%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 0.1328,
      round3: -0.017100000000000004,
    },
    {
      ticker: 'NDX 3m BearShark',
      name: '3M Bearish Sharkfin on NDX| 100% Strike| 60% KO| 97%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 0.332,
      round3: -0.02959999999999996,
    },
    {
      ticker: 'SX5E 3m BearShark',
      name: '3M Bearish Sharkfin on SX5E| 100% Strike| 70% KO| 97.6%MRN ',
      theme: 'Europe',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 0.1992,
      round3: -0.02400000000000002,
    },
    {
      ticker: 'HSCEI 3m BearShark',
      name: '3M Bearish Sharkfin on HSCEI| 100% Strike| 70% KO| 97% MRN',
      theme: 'China',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 0.22530000000000006,
      round3: -0.03069999999999995,
    },
    {
      ticker: 'NKY 3m BearShark',
      name: '3M Bearish Sharkfin on NKY| 100% Strike| 70% KO| 96.8% MRN',
      theme: 'Japan',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 0.21420000000000008,
      round3: -0.032099999999999906,
    },
    {
      ticker: 'SPX 6m BearShark',
      name: '6M Bearish Sharkfin on SPX| 100% Strike| 70% KO| 99%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0.18280000000000005,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'NDX 6m BearShark',
      name: '6M Bearish Sharkfin on NDX| 100% Strike| 60% KO| 97.5%MRN ',
      theme: 'US',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: -0.025000000000000022,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'SX5E 6m BearShark',
      name: '6M Bearish Sharkfin on SX5E| 100% Strike| 70% KO| 98.2%MRN ',
      theme: 'Europe',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0.13089999999999993,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'HSCEI 6m BearShark',
      name: '6M Bearish Sharkfin on HSCEI| 100% Strike| 70% KO| 97% MRN',
      theme: 'China',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0.030599999999999995,
      round2: 0,
      round3: 0,
    },
    {
      ticker: 'NKY 6m BearShark',
      name: '6M Bearish Sharkfin on NKY| 100% Strike| 70% KO| 97.8% MRN',
      theme: 'Japan',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0.15180000000000005,
      round2: 0,
      round3: 0,
    },
  ],
  growth: [
    {
      ticker: 'Energy23m',
      name: '3M BEN CVX US + OXY US + SLB US| 100% Strike| 26%',
      theme: 'Energy',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: -0.2963,
      round2: -0.1287,
      round3: 0.26,
    },
    {
      ticker: 'Biotech3m',
      name: '3M BEN NOVOB DC + BAYN GY| 100% Strike| 50%',
      theme: 'Biotech',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.3217,
      round3: 0.5,
    },
    {
      ticker: 'Energy2',
      name: '6M BEN CVX US + OXY US + SLB US| 100% Strike| 40%',
      theme: 'Energy',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '2',
      round1: 0,
      round2: -0.1287,
      round3: 0.6067944450820613,
    },
    {
      ticker: 'Biotech',
      name: '6M BEN (2m lookback) NOVOB DC + BAYN GY| 100% Strike| 20.74%',
      theme: 'Biotech',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '2',
      round1: 0,
      round2: -0.1217,
      round3: 0.37470112717750204,
    },
    {
      ticker: 'EU Consumer',
      name: '3M Booster ADS GY + MC FP + RMS FP| 100% Strike| 270% PR',
      theme: 'EU Consumer',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: -0.3312,
      round2: -0.4135,
      round3: 0.8551000000000001,
    },
    {
      ticker: 'Financial2',
      name: '3M Booster C US + GS US + DBK GY| 100% Strike| 260% PR',
      theme: 'US Financial',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: -0.2522,
      round2: -0.2888,
      round3: 0.6520999999999999,
    },
    {
      ticker: 'Industrial2',
      name: '3M Booster FDX US + SU FP + SIE GY| 100% Strike| 440% PR',
      theme: 'Industrial',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: -0.1871,
      round2: -0.4222,
      round3: 1.54,
    },
    {
      ticker: 'Mining2',
      name: '3M Booster LIN US + RIO LN + AAL LN| 100% Strike| 475% PR',
      theme: 'Mining',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: -0.2215,
      round3: 1.0921,
    },
    {
      ticker: 'US Tech2',
      name: '3M Booster NVDA US + TSLA US + AMZN US| 100% Strike| 490% PR',
      theme: 'US Tech',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '2, 3',
      round1: -0.2336,
      round2: -0.5853,
      round3: 3.5496,
    },
    {
      ticker: 'Tech Tracker',
      name: 'Tracker Cert on AI Basket',
      theme: 'Technology',
      allocation: 0,
      premium: 0,
      tenor: 12,
      display: '1, 3',
      round1: -0.18332727272727273,
      round2: 0,
      round3: 0.65,
    },
    {
      ticker: 'Industrial Tracker',
      name: 'Tracker Cert on Industrial Basket',
      theme: 'Industrial',
      allocation: 0,
      premium: 0,
      tenor: 12,
      display: '1, 3',
      round1: -0.04008,
      round2: 0,
      round3: 0.29,
    },
    {
      ticker: 'Mining Tracker',
      name: 'Tracker Cert on Mining & Energy Basket',
      theme: 'MetalsAndMining',
      allocation: 0,
      premium: 0,
      tenor: 12,
      display: '1, 3',
      round1: 0.03465999999999999,
      round2: 0,
      round3: 0.08,
    },
    {
      ticker: 'Consumer Tracker',
      name: 'Tracker Cert on Global Consumer Basket',
      theme: 'Consumer',
      allocation: 0,
      premium: 0,
      tenor: 12,
      display: '1, 3',
      round1: -0.05168235294117647,
      round2: 0,
      round3: 0.23,
    },
    {
      ticker: 'Financial Tracker',
      name: 'Tracker Cert on Global Financials Basket',
      theme: 'Financial',
      allocation: 0,
      premium: 0,
      tenor: 12,
      display: '1, 3',
      round1: -0.07644444444444444,
      round2: 0,
      round3: 0.15,
    },
  ],
  hedging: [
    {
      ticker: 'SPX 6m Put',
      name: '6M Put on S&P 500| 100% Strike| 4.81% premium',
      theme: 'SPX Put',
      allocation: 0,
      premium: 0.0481,
      tenor: 6,
      display: '1, 2',
      round1: 2.997920997920998,
      round2: 0.5582120582120583,
      round3: -1,
    },
    {
      ticker: 'NDX 6m Put',
      name: '6M Put on Nasdaq 100| 100% Strike| 6.23% premium',
      theme: 'NDX Put',
      allocation: 0,
      premium: 0.0623,
      tenor: 6,
      display: '1, 2',
      round1: 7.979133226324238,
      round2: 1.9020866773675762,
      round3: -1,
    },
    {
      ticker: 'SX5E 6m Put',
      name: '6M Put on Euro Stoxx 50| 100% Strike| 5.55% premium',
      theme: 'SX5E Put',
      allocation: 0,
      premium: 0.0555,
      tenor: 6,
      display: '1, 2',
      round1: 1.6774774774774772,
      round2: 1.010810810810811,
      round3: -1,
    },
    {
      ticker: 'HSCEI 6m Put',
      name: '6M Put on HangSeng China Enterprise| 100% Strike| 7.41% premium',
      theme: 'HSCEI Put',
      allocation: 0,
      premium: 0.0741,
      tenor: 6,
      display: '1, 2',
      round1: -0.1794871794871795,
      round2: 0.727395411605938,
      round3: -1,
    },
    {
      ticker: 'NKY 6m Put',
      name: '6M Put on Nikkei 225| 100% Strike| 7.98% premium',
      theme: 'NKY Put',
      allocation: 0,
      premium: 0.0798,
      tenor: 6,
      display: '1, 2',
      round1: 1.1867167919799495,
      round2: 0.5432330827067666,
      round3: -1,
    },
    {
      ticker: 'SPX 3m Put',
      name: '3M Put on S&P 500| 100% Strike| 3.23% premium',
      theme: 'SPX Put',
      allocation: 0,
      premium: 0.0323,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 3.6408668730650153,
      round3: -1,
    },
    {
      ticker: 'NDX 3m Put',
      name: '3M Put on Nasdaq 100| 100% Strike| 4.35% premium',
      theme: 'NDX Put',
      allocation: 0,
      premium: 0.0435,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 7.31264367816092,
      round3: -1,
    },
    {
      ticker: 'SX5E 3m Put',
      name: '3M Put on Euro Stoxx 50| 100% Strike| 3.67% premium',
      theme: 'SX5E Put',
      allocation: 0,
      premium: 0.0367,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 5.0817438692098085,
      round3: -1,
    },
    {
      ticker: 'HSCEI 3m Put',
      name: '3M Put on HangSeng China Enterprise| 100% Strike| 4.69% premium',
      theme: 'HSCEI Put',
      allocation: 0,
      premium: 0.0469,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 4.458422174840086,
      round3: -1,
    },
    {
      ticker: 'NKY 3m Put',
      name: '3M Put on Nikkei 225| 100% Strike| 5.91% premium',
      theme: 'NKY Put',
      allocation: 0,
      premium: 0.0591,
      tenor: 3,
      display: '2, 3',
      round1: 0,
      round2: 3.1675126903553297,
      round3: -1,
    },
  ],
  income: [
    {
      ticker: 'China Tech',
      name: '6M FCN 700 HK + 9988 HK + 9888 HK| 75% Strike| 16% p.a.',
      theme: 'China Tech',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.08,
      round2: -0.3359,
      round3: 0.6262611052552327,
    },
    {
      ticker: 'US Tech1',
      name: '6M FCN NVDA US + MU US| 70% Strike| 25% p.a.',
      theme: 'US Tech',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.125,
      round2: -0.2793,
      round3: 0.5609823782433745,
    },
    {
      ticker: 'China Tech3m',
      name: '3M FCN 700 HK + 9988 HK + 9888 HK| 77% Strike| 16% p.a.',
      theme: 'China Tech',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0.08,
      round3: 0.08,
    },
    {
      ticker: 'US Tech3m',
      name: '3M FCN NVDA US + MU US| 72% Strike| 25% p.a.',
      theme: 'US Tech',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0.125,
      round3: 0.125,
    },
    {
      ticker: 'US Tech3',
      name: '6M V-BRC NVDA US +  MU US + META US| 100% Strike| 80%AKI| 25% p.a.',
      theme: 'US Tech',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '2',
      round1: 0.125,
      round2: -0.3375,
      round3: 1.8220377358490567,
    },
    {
      ticker: 'Industrial1',
      name: '6M SDAC CAT US + HON US + SIE GY| 100% Strike| 70%AKI| 31% p.a.',
      theme: 'Industrial',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.155,
      round2: -0.2356,
      round3: 0.5109890109890112,
    },
    {
      ticker: 'Healthcare',
      name: '6M SDAC GSK LN + AMGN US + BAYN GY| 100% Strike| 70%AKI| 54% p.a.',
      theme: 'Healthcare',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.27,
      round2: -0.2217,
      round3: 0.6317615315431069,
    },
    {
      ticker: 'US Consumer',
      name: '6M SDAC SBUX US + COST US + WMT US| 100% Strike| 70%AKI| 18% p.a.',
      theme: 'US Consumer',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.09,
      round2: -0.2504,
      round3: 0.4541088580576307,
    },
    {
      ticker: 'Industrial3m',
      name: '3M SDAC CAT US + HON US + SIE GY| 100%Strike| 70%AKI| 24% p.a.',
      theme: 'Industrial',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.06,
    },
    {
      ticker: 'Healthcare1',
      name: '3M SDAC GSK LN + AMGN US + BAYN GY. 100%Strike| 70%AKI| 30% p.a.',
      theme: 'Healthcare',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.075,
    },
    {
      ticker: 'US Consumer3m',
      name: '3M SDAC SBUX US + COST US + WMT US| 100% Strike| 70%AKI| 18% p.a.',
      theme: 'US Consumer',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.045,
    },
    {
      ticker: 'EU Financial',
      name: '6M DRA BNP FP + DBK GY + UBSG SW| 80%Strike| 20% p.a.',
      theme: 'EU Financial',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.1,
      round2: -0.1888,
      round3: 0.3560157790927023,
    },
    {
      ticker: 'US Financial',
      name: '6M DRA C US + GS US + JPM US| 80%Strike| 14.5% p.a.',
      theme: 'US Financial',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.0725,
      round2: -0.2311,
      round3: 0.3948497854077253,
    },
    {
      ticker: 'SG Banks',
      name: '6M DRA DBS SP + OCBC SP + UOB SP| 95%Strike| 11% p.a.',
      theme: 'SG Banks',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.055,
      round2: 0,
      round3: 0.05499999999999994,
    },
    {
      ticker: 'EU Financial3m',
      name: '3M DRA BNP FP + DBK GY + UBSG SW| 80%Strike| 14% p.a.',
      theme: 'EU Financial',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.035,
    },
    {
      ticker: 'US Financial3m',
      name: '3M DRA C US + GS US + JPM US| 80%Strike|  13.5% p.a.',
      theme: 'US Financial',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.03375,
    },
    {
      ticker: 'Energy1',
      name: '6M DCN on CVX UN + OXY US| 100% Strike| 24%',
      theme: 'Energy',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.24,
      round2: -0.0765,
      round3: 0.34271792095289655,
    },
    {
      ticker: 'Mining1',
      name: '6M DCN on NEM US + RIO LN| 95% Strike| 45%',
      theme: 'Mining',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.45,
      round2: -0.142,
      round3: 0.6899766899766899,
    },
    {
      ticker: 'Utility',
      name: '6M DCN on RWE GY + VIE FP| 100% Strike| 34%',
      theme: 'Utilities',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1, 2',
      round1: 0.34,
      round2: 0.34,
      round3: 0,
    },
    {
      ticker: 'Energy13m',
      name: '3M DCN on CVX UN + OXY US| 100% Strike| 19%',
      theme: 'Energy',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.19,
    },
    {
      ticker: 'Mining13m',
      name: '3M DCN on NEM US + RIO LN| 95% Strike| 40%',
      theme: 'Mining',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.4,
    },
    {
      ticker: 'Utility3m',
      name: '3M DCN on RWE GY + VIE FP| 100% Strike| 32%',
      theme: 'Utilities',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '3',
      round1: 0,
      round2: 0,
      round3: 0.32,
    },
    {
      ticker: '3M Term',
      name: '3M Term Note| 3.5% p.a.',
      theme: 'Cash alternatives',
      allocation: 0,
      premium: 0,
      tenor: 3,
      display: '',
      round1: 0.00875,
      round2: 0.00875,
      round3: 0.00875,
    },
    {
      ticker: '6M Term',
      name: '6M Term Note| 3.2% p.a.',
      theme: 'Cash alternatives',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '',
      round1: 0.016,
      round2: 0.008,
      round3: 0.007936507936507908,
    },
    {
      ticker: 'CRAN',
      name: '5Y CRAN 6% p.a. accrue daily if USDCMS10Y <= 5%',
      theme: 'Cash alternatives',
      allocation: 0,
      premium: 0,
      tenor: 60,
      display: '1, 2, 3',
      round1: 0.0125,
      round2: 0.0125,
      round3: 0.01728395061728394,
    },
    {
      ticker: 'BLNUST10Y',
      name: '6M BLN on UST10Y| 5.3% p.a.',
      theme: 'US Treasury',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '1',
      round1: 0.0265,
      round2: 0.01325,
      round3: 0.013076733283987174,
    },
    {
      ticker: 'BLNUST20Y',
      name: '6M BLN on UST20Y| 5.5% p.a.',
      theme: 'US Treasury',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '',
      round1: 0.0275,
      round2: 0.01375,
      round3: 0.01356350184956856,
    },
    {
      ticker: 'BLNUST30Y',
      name: '6M BLN on UST30Y| 6.0% p.a.',
      theme: 'US Treasury',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '',
      round1: 0.03,
      round2: 0.015,
      round3: 0.014778325123152802,
    },
    {
      ticker: 'BLNJPM34',
      name: '6M BLN on Jpmorgan Chase & Co 5.35%| 6.28% p.a.',
      theme: 'US Financial',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '',
      round1: 0.0314,
      round2: 0.0157,
      round3: 0.015457320074825365,
    },
    {
      ticker: 'BLNUBS29',
      name: '6M BLN on UBS Group AG 6.246%| 6.44% p.a.',
      theme: 'EU Financial',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '',
      round1: 0.0322,
      round2: 0.0161,
      round3: 0.01584489715579185,
    },
    {
      ticker: 'BLNHSBC36',
      name: '6M BLN on HSBC Holdings Plc 5.741%| 6.36% p.a.',
      theme: 'EU Financial',
      allocation: 0,
      premium: 0,
      tenor: 6,
      display: '',
      round1: 0.0318,
      round2: 0.0159,
      round3: 0.015651146766413992,
    },
  ],
};


const STOCK_RETURNS = {
  consumer: {
    round1: {
      '1211 HK': -0.11939999999999999,
      '7203 JP': 0.022000000000000002,
      'BMW GY': -0.1079,
      'MBG GY': -0.0037,
      '7974 JP': 0.0425,
      'ADS GY': -0.0421,
      'NKE US': 0.0182,
      'CFR SW': -0.2441,
      'MC FP': -0.3312,
      'RMS FP': -0.0926,
      'MCD US': -0.11470000000000001,
      'SBUX US': 0.0593,
      'CA FP': -0.2616,
      'COST US': 0.1234,
      'WMT US': 0.052199999999999996,
      'CL US': 0.1777,
      'PG US': -0.056600000000000004,
    },
    round2: {
      '1211 HK': -0.3135,
      '7203 JP': -0.29960000000000003,
      'BMW GY': -0.28309999999999996,
      'MBG GY': -0.39740000000000003,
      '7974 JP': -0.2433,
      'ADS GY': -0.21719999999999998,
      'NKE US': 0.1175,
      'CFR SW': -0.3367,
      'MC FP': -0.41350000000000003,
      'RMS FP': -0.2086,
      'MCD US': 0.003,
      'SBUX US': -0.3504,
      'CA FP': -0.1536,
      'COST US': -0.1344,
      'WMT US': 0.0159,
      'CL US': -0.009300000000000001,
      'PG US': 0.1471,
    },
    round3: {
      '1211 HK': 0.4393,
      '7203 JP': 0.08470000000000001,
      'BMW GY': 0.3968,
      'MBG GY': 0.479,
      '7974 JP': 0.34130000000000005,
      'ADS GY': 0.48829999999999996,
      'NKE US': 0.2041,
      'CFR SW': 0.026000000000000002,
      'MC FP': 0.31670000000000004,
      'RMS FP': 0.3411,
      'MCD US': -0.016399999999999998,
      'SBUX US': 0.2751,
      'CA FP': 0.10400000000000001,
      'COST US': 0.2481,
      'WMT US': 0.1641,
      'CL US': -0.0055000000000000005,
      'PG US': 0.09300000000000001,
    },
  },
  financials: {
    round1: {
      'C US': -0.1636,
      'GS US': -0.2522,
      'JPM US': -0.013999999999999999,
      'DBS SP': -0.083,
      'OCBC SP': -0.0793,
      'UOB SP': -0.0776,
      'BNP FP': -0.046,
      'DBK GY': -0.0779,
      'UBSG SW': 0.1056,
    },
    round2: {
      'C US': -0.2311,
      'GS US': -0.1672,
      'JPM US': -0.2263,
      'DBS SP': -0.0605,
      'OCBC SP': -0.057699999999999994,
      'UOB SP': -0.0565,
      'BNP FP': -0.1265,
      'DBK GY': -0.2888,
      'UBSG SW': -0.1134,
    },
    round3: {
      'C US': 0.25079999999999997,
      'GS US': 0.3019,
      'JPM US': 0.07490000000000001,
      'DBS SP': 0.0706,
      'OCBC SP': 0.0674,
      'UOB SP': 0.066,
      'BNP FP': 0.1192,
      'DBK GY': 0.3189,
      'UBSG SW': 0.1085,
    },
  },
  healthcare: {
    round1: {
      'AMGN US': -0.1381,
      'AZN LN': -0.0138,
      'BAYN GY': 0.1421,
      'GSK LN': -0.1011,
      'JNJ US': -0.0625,
      'LLY US': -0.049,
      'NOVOB DC': 0.015,
      'PFE US': -0.0842,
    },
    round2: {
      'AMGN US': -0.0315,
      'AZN LN': -0.0401,
      'BAYN GY': -0.32170000000000004,
      'GSK LN': -0.035699999999999996,
      'JNJ US': 0.1116,
      'LLY US': 0.09449999999999999,
      'NOVOB DC': -0.1285,
      'PFE US': 0.0039000000000000003,
    },
    round3: {
      'AMGN US': -0.039599999999999996,
      'AZN LN': -0.0212,
      'BAYN GY': 0.1387,
      'GSK LN': -0.09820000000000001,
      'JNJ US': 0.07,
      'LLY US': -0.0233,
      'NOVOB DC': 0.0073,
      'PFE US': -0.0037,
    },
  },
  industrials: {
    round1: {
      'GE US': -0.271,
      'FDX US': -0.06,
      'UPS US': 0.0154,
      'CAT US': 0.45880000000000004,
      'ABBN SW': -0.2305,
      'EMR US': -0.0646,
      'SU FP': -0.0756,
      '6501 JP': -0.1402,
      'HON US': 0.154,
      'SIE GY': -0.18710000000000002,
    },
    round2: {
      'GE US': -0.231,
      'FDX US': -0.0858,
      'UPS US': -0.09759999999999999,
      'CAT US': -0.099,
      'ABBN SW': -0.5735,
      'EMR US': -0.2168,
      'SU FP': -0.3874,
      '6501 JP': -0.35009999999999997,
      'HON US': -0.23559999999999998,
      'SIE GY': -0.42219999999999996,
    },
    round3: {
      'GE US': 0.08220000000000001,
      'FDX US': 0.4117,
      'UPS US': 0.0521,
      'CAT US': 0.1751,
      'ABBN SW': 0.37929999999999997,
      'EMR US': 0.22219999999999998,
      'SU FP': 0.35,
      '6501 JP': 0.21059999999999998,
      'HON US': 0.2889,
      'SIE GY': 0.7748999999999999,
    },
  },
  metalsAndMining: {
    round1: {
      'AEM CN': 0.11470000000000001,
      'NEM US': -0.0484,
      'ANTO LN': 0.0654,
      'HOLN SW': -0.028399999999999998,
      'BAS GY': 0.12380000000000001,
      'AAL LN': 0.1409,
      'BHP AU': 0.0756,
      'RIO LN': 0.2742,
      'AI FP': 0.19940000000000002,
      'LIN US': 0.2041,
      'DD US': 0.2875,
      'BP/ LN': -0.021099999999999997,
      'CVX US': 0.0458,
      'OXY US': 0.1591,
      'SHELL NA': -0.0803,
      'TTE FP': -0.0742,
      'XOM US': -0.0817,
      'HAL US': -0.24420000000000003,
      'SLB US': -0.2963,
      'COP US': -0.12269999999999999,
    },
    round2: {
      'AEM CN': 0.2631,
      'NEM US': 0.27,
      'ANTO LN': -0.0692,
      'HOLN SW': -0.16940000000000002,
      'BAS GY': -0.1659,
      'AAL LN': -0.22149999999999997,
      'BHP AU': -0.17420000000000002,
      'RIO LN': -0.142,
      'AI FP': -0.0925,
      'LIN US': -0.1032,
      'DD US': -0.0043,
      'BP/ LN': -0.0323,
      'CVX US': -0.056900000000000006,
      'OXY US': -0.0765,
      'SHELL NA': -0.1788,
      'TTE FP': -0.1082,
      'XOM US': -0.0928,
      'HAL US': -0.36369999999999997,
      'SLB US': -0.12869999999999998,
      'COP US': -0.0537,
    },
    round3: {
      'AEM CN': -0.043899999999999995,
      'NEM US': -0.18899999999999997,
      'ANTO LN': 0.2299,
      'HOLN SW': 0.1776,
      'BAS GY': 0.0741,
      'AAL LN': 0.2726,
      'BHP AU': 0.2413,
      'RIO LN': 0.2299,
      'AI FP': 0.022099999999999998,
      'LIN US': 0.31980000000000003,
      'DD US': 0.041299999999999996,
      'BP/ LN': -0.0434,
      'CVX US': 0.06559999999999999,
      'OXY US': 0.1003,
      'SHELL NA': 0.0304,
      'TTE FP': 0.0875,
      'XOM US': 0.0033,
      'HAL US': -0.4156,
      'SLB US': 0.20679999999999998,
      'COP US': 0.11720000000000001,
    },
  },
  technology: {
    round1: {
      'GOOGL US': -0.18530000000000002,
      'AAPL US': -0.1427,
      'AMZN US': -0.2336,
      'MSFT US': -0.0933,
      'META US': -0.1979,
      'NVDA US': -0.2072,
      'TSLA US': -0.251,
      'MU US': -0.09720000000000001,
      '9988 HK': -0.2237,
      '9888 HK': -0.2509,
      '700 HK': -0.1338,
    },
    round2: {
      'GOOGL US': -0.4321,
      'AAPL US': -0.3328,
      'AMZN US': -0.5777,
      'MSFT US': -0.299,
      'META US': -0.46140000000000003,
      'NVDA US': -0.40759999999999996,
      'TSLA US': -0.5853,
      'MU US': -0.5418,
      '9988 HK': -0.5217,
      '9888 HK': -0.585,
      '700 HK': -0.3119,
    },
    round3: {
      'GOOGL US': 0.5349,
      'AAPL US': 0.4119,
      'AMZN US': 0.8094,
      'MSFT US': 0.29469999999999996,
      'META US': 0.5710999999999999,
      'NVDA US': 1.4354,
      'TSLA US': 0.7243999999999999,
      'MU US': 0.6463,
      '9988 HK': 0.6456999999999999,
      '9888 HK': 0.7241,
      '700 HK': 0.386,
    },
  },
  utilities: {
    round1: {
      'ENEL IM': -0.1517,
      'IBE SM': 0.1362,
      'NEE US': -0.052199999999999996,
      'SSE LN': 0.1421,
      'RWE GY': 0.0483,
      'ENGI FP': 0.037200000000000004,
      'EOAN GY': -0.0781,
      'VIE FP': 0.1821,
    },
    round2: {
      'ENEL IM': -0.1198,
      'IBE SM': 0.0078000000000000005,
      'NEE US': -0.1016,
      'SSE LN': -0.0016,
      'RWE GY': -0.0613,
      'ENGI FP': -0.0472,
      'EOAN GY': -0.0815,
      'VIE FP': -0.1446,
    },
    round3: {
      'ENEL IM': -0.0106,
      'IBE SM': -0.022099999999999998,
      'NEE US': 0.064,
      'SSE LN': -0.058600000000000006,
      'RWE GY': -0.0431,
      'ENGI FP': -0.0332,
      'EOAN GY': 0.0226,
      'VIE FP': -0.11900000000000001,
    },
  },
};


const BOND_RETURNS = {
  corporate: {
    round1: {
      'MSFT35': 0.01077353760130411,
      'Tencent30': 0.01020001709006775,
      'CkHut33': 0.011676512,
      'BIDU28': 0.0100526615,
      'AIA34': 0.0121876332,
      'JPM34': 0.01191821380841105,
      'JD30': 0.01047291117219078,
      'DAI28': 0.01028295790126197,
      'UBS29': 0.01047169568293569,
      'HSBC36': 0.01341098583970972,
      'GM29': 0.010913209275,
      'SocGen29': 0.01077775112570694,
      'DELL34': 0.0125482901,
    },
    round2: {
      'MSFT35': 0.01077353760130411,
      'Tencent30': 0.01020001709006775,
      'CkHut33': 0.011676512,
      'BIDU28': 0.0100526615,
      'AIA34': 0.0121876332,
      'JPM34': 0.01191821380841105,
      'JD30': 0.01047291117219078,
      'DAI28': 0.01028295790126197,
      'UBS29': 0.01047169568293569,
      'HSBC36': 0.01341098583970972,
      'GM29': 0.010913209275,
      'SocGen29': 0.01077775112570694,
      'DELL34': 0.0125482901,
    },
    round3: {
      'MSFT35': 0.01077353760130411,
      'Tencent30': 0.01020001709006775,
      'CkHut33': 0.011676512,
      'BIDU28': 0.0100526615,
      'AIA34': 0.0121876332,
      'JPM34': 0.01191821380841105,
      'JD30': 0.01047291117219078,
      'DAI28': 0.01028295790126197,
      'UBS29': 0.01047169568293569,
      'HSBC36': 0.01341098583970972,
      'GM29': 0.010913209275,
      'SocGen29': 0.01077775112570694,
      'DELL34': 0.0125482901,
    },
  },
  treasuries: {
    round1: {
      'US Bill 3m': 0.006999999999999999,
      'US Bill 6m': 0.006500000000000001,
      'US Bill 12m': 0.008,
      'UST5Y': 0.009571039810401024,
      'UST10Y': 0.0106330525,
      'UST20Y': 0.0120799215,
      'UST30Y': 0.0121811305,
    },
    round2: {
      'US Bill 3m': 0.008877633877932396,
      'US Bill 6m': 0.009168582013768729,
      'US Bill 12m': 0.009220618580940608,
      'UST5Y': 0.009571039810401024,
      'UST10Y': 0.0106330525,
      'UST20Y': 0.0120799215,
      'UST30Y': 0.0121811305,
    },
    round3: {
      'US Bill 3m': 0.008877633877932396,
      'US Bill 6m': 0.009168582013768729,
      'US Bill 12m': 0.009220618580940608,
      'UST5Y': 0.009571039810401024,
      'UST10Y': 0.0106330525,
      'UST20Y': 0.0120799215,
      'UST30Y': 0.0121811305,
    },
  },
};


const SP_RETURNS = {
  round1: {
    '3M Term': 0.00875,
    '6M Term': 0.016,
    'BLNHSBC36': 0.0318,
    'BLNJPM34': 0.0314,
    'BLNUBS29': 0.0322,
    'BLNUST10Y': 0.0265,
    'BLNUST20Y': 0.0275,
    'BLNUST30Y': 0.03,
    'Biotech': 0,
    'Biotech3m': 0,
    'CRAN': 0.0125,
    'China': 0,
    'China Tech': 0.08,
    'China Tech3m': 0,
    'Consumer Tracker': -0.05168235294117647,
    'EU Consumer': -0.3312,
    'EU Financial': 0.1,
    'EU Financial3m': 0,
    'Energy1': 0.24,
    'Energy13m': 0,
    'Energy2': 0,
    'Energy23m': -0.2963,
    'Financial Tracker': -0.07644444444444444,
    'Financial2': -0.2522,
    'Gold': 0.15,
    'HSCEI 3m BearShark': 0,
    'HSCEI 3m BullShark': 0,
    'HSCEI 3m Put': 0,
    'HSCEI 6m BearShark': 0.030599999999999995,
    'HSCEI 6m BullShark': 0,
    'HSCEI 6m Put': -0.1794871794871795,
    'Healthcare': 0.27,
    'Healthcare1': 0,
    'Industrial Tracker': -0.04008,
    'Industrial1': 0.155,
    'Industrial2': -0.1871,
    'Industrial3m': 0,
    'Mining Tracker': 0.03465999999999999,
    'Mining1': 0.45,
    'Mining13m': 0,
    'Mining2': 0,
    'NDX 3m BearShark': 0,
    'NDX 3m BullShark': 0,
    'NDX 3m Put': 0,
    'NDX 6m BearShark': -0.025000000000000022,
    'NDX 6m BullShark': -0.062000000000000055,
    'NDX 6m Put': 7.979133226324238,
    'NKY 3m BearShark': 0,
    'NKY 3m BullShark': 0,
    'NKY 3m Put': 0,
    'NKY 6m BearShark': 0.15180000000000005,
    'NKY 6m BullShark': 0,
    'NKY 6m Put': 1.1867167919799495,
    'SG Banks': 0.055,
    'SPX 3m BearShark': 0,
    'SPX 3m BullShark': 0,
    'SPX 3m Put': 0,
    'SPX 6m BearShark': 0.18280000000000005,
    'SPX 6m BullShark': -0.02400000000000002,
    'SPX 6m Put': 2.997920997920998,
    'SX5E 3m BearShark': 0,
    'SX5E 3m BullShark': 0,
    'SX5E 3m Put': 0,
    'SX5E 6m BearShark': 0.13089999999999993,
    'SX5E 6m BullShark': -0.02510000000000001,
    'SX5E 6m Put': 1.6774774774774772,
    'Tech Tracker': -0.18332727272727273,
    'US Consumer': 0.09,
    'US Consumer3m': 0,
    'US Financial': 0.0725,
    'US Financial3m': 0,
    'US Tech1': 0.125,
    'US Tech2': -0.2336,
    'US Tech3': 0.125,
    'US Tech3m': 0,
    'Utility': 0.34,
    'Utility3m': 0,
  },
  round2: {
    '3M Term': 0.00875,
    '6M Term': 0.008,
    'BLNHSBC36': 0.0159,
    'BLNJPM34': 0.0157,
    'BLNUBS29': 0.0161,
    'BLNUST10Y': 0.01325,
    'BLNUST20Y': 0.01375,
    'BLNUST30Y': 0.015,
    'Biotech': -0.1217,
    'Biotech3m': -0.3217,
    'CRAN': 0.0125,
    'China': -0.1,
    'China Tech': -0.3359,
    'China Tech3m': 0.08,
    'Consumer Tracker': 0,
    'EU Consumer': -0.4135,
    'EU Financial': -0.1888,
    'EU Financial3m': 0,
    'Energy1': -0.0765,
    'Energy13m': 0,
    'Energy2': -0.1287,
    'Energy23m': -0.1287,
    'Financial Tracker': 0,
    'Financial2': -0.2888,
    'Gold': 0.0831,
    'HSCEI 3m BearShark': 0.22530000000000006,
    'HSCEI 3m BullShark': -0.014000000000000012,
    'HSCEI 3m Put': 4.458422174840086,
    'HSCEI 6m BearShark': 0,
    'HSCEI 6m BullShark': 0,
    'HSCEI 6m Put': 0.727395411605938,
    'Healthcare': -0.2217,
    'Healthcare1': 0,
    'Industrial Tracker': 0,
    'Industrial1': -0.2356,
    'Industrial2': -0.4222,
    'Industrial3m': 0,
    'Mining Tracker': 0,
    'Mining1': -0.142,
    'Mining13m': 0,
    'Mining2': -0.2215,
    'NDX 3m BearShark': 0.332,
    'NDX 3m BullShark': -0.04700000000000004,
    'NDX 3m Put': 7.31264367816092,
    'NDX 6m BearShark': 0,
    'NDX 6m BullShark': 0,
    'NDX 6m Put': 1.9020866773675762,
    'NKY 3m BearShark': 0.21420000000000008,
    'NKY 3m BullShark': -0.015000000000000013,
    'NKY 3m Put': 3.1675126903553297,
    'NKY 6m BearShark': 0,
    'NKY 6m BullShark': 0,
    'NKY 6m Put': 0.5432330827067666,
    'SG Banks': 0,
    'SPX 3m BearShark': 0.1328,
    'SPX 3m BullShark': -0.030000000000000027,
    'SPX 3m Put': 3.6408668730650153,
    'SPX 6m BearShark': 0,
    'SPX 6m BullShark': 0,
    'SPX 6m Put': 0.5582120582120583,
    'SX5E 3m BearShark': 0.1992,
    'SX5E 3m BullShark': -0.025499999999999967,
    'SX5E 3m Put': 5.0817438692098085,
    'SX5E 6m BearShark': 0,
    'SX5E 6m BullShark': 0,
    'SX5E 6m Put': 1.010810810810811,
    'Tech Tracker': 0,
    'US Consumer': -0.2504,
    'US Consumer3m': 0,
    'US Financial': -0.2311,
    'US Financial3m': 0,
    'US Tech1': -0.2793,
    'US Tech2': -0.5853,
    'US Tech3': -0.3375,
    'US Tech3m': 0.125,
    'Utility': 0.34,
    'Utility3m': 0,
  },
  round3: {
    '3M Term': 0.00875,
    '6M Term': 0.007936507936507908,
    'BLNHSBC36': 0.015651146766413992,
    'BLNJPM34': 0.015457320074825365,
    'BLNUBS29': 0.01584489715579185,
    'BLNUST10Y': 0.013076733283987174,
    'BLNUST20Y': 0.01356350184956856,
    'BLNUST30Y': 0.014778325123152802,
    'Biotech': 0.37470112717750204,
    'Biotech3m': 0.5,
    'CRAN': 0.01728395061728394,
    'China': 0.06343216,
    'China Tech': 0.6262611052552327,
    'China Tech3m': 0.08,
    'Consumer Tracker': 0.23,
    'EU Consumer': 0.8551000000000001,
    'EU Financial': 0.3560157790927023,
    'EU Financial3m': 0.035,
    'Energy1': 0.34271792095289655,
    'Energy13m': 0.19,
    'Energy2': 0.6067944450820613,
    'Energy23m': 0.26,
    'Financial Tracker': 0.15,
    'Financial2': 0.6520999999999999,
    'Gold': -0.07672421752377434,
    'HSCEI 3m BearShark': -0.03069999999999995,
    'HSCEI 3m BullShark': 0.008599999999999986,
    'HSCEI 3m Put': -1,
    'HSCEI 6m BearShark': 0,
    'HSCEI 6m BullShark': 0,
    'HSCEI 6m Put': -1,
    'Healthcare': 0.6317615315431069,
    'Healthcare1': 0.075,
    'Industrial Tracker': 0.29,
    'Industrial1': 0.5109890109890112,
    'Industrial2': 1.54,
    'Industrial3m': 0.06,
    'Mining Tracker': 0.08,
    'Mining1': 0.6899766899766899,
    'Mining13m': 0.4,
    'Mining2': 1.0921,
    'NDX 3m BearShark': -0.02959999999999996,
    'NDX 3m BullShark': 0.3027999999999999,
    'NDX 3m Put': -1,
    'NDX 6m BearShark': 0,
    'NDX 6m BullShark': 0,
    'NDX 6m Put': -1,
    'NKY 3m BearShark': -0.032099999999999906,
    'NKY 3m BullShark': 0.06359999999999999,
    'NKY 3m Put': -1,
    'NKY 6m BearShark': 0,
    'NKY 6m BullShark': 0,
    'NKY 6m Put': -1,
    'SG Banks': 0.05499999999999994,
    'SPX 3m BearShark': -0.017100000000000004,
    'SPX 3m BullShark': 0.07289999999999996,
    'SPX 3m Put': -1,
    'SPX 6m BearShark': 0,
    'SPX 6m BullShark': 0,
    'SPX 6m Put': -1,
    'SX5E 3m BearShark': -0.02400000000000002,
    'SX5E 3m BullShark': 0.12900000000000003,
    'SX5E 3m Put': -1,
    'SX5E 6m BearShark': 0,
    'SX5E 6m BullShark': 0,
    'SX5E 6m Put': -1,
    'Tech Tracker': 0.65,
    'US Consumer': 0.4541088580576307,
    'US Consumer3m': 0.045,
    'US Financial': 0.3948497854077253,
    'US Financial3m': 0.03375,
    'US Tech1': 0.5609823782433745,
    'US Tech2': 3.5496,
    'US Tech3': 1.8220377358490567,
    'US Tech3m': 0.125,
    'Utility': 0,
    'Utility3m': 0.32,
  },
};


const SP_PERFORMANCE_RANKINGS = {
  round1: {
    top3: [
      { ticker: 'Mining', name: '6M DCN on NEM US + RIO LN, 95% Strike, 45%', return: 0.45, isBad: false, badWarning: '' },
      { ticker: 'Utilities', name: '6M DCN on RWE GY + VIE FP, 100% Strike, 34%', return: 0.34, isBad: false, badWarning: '' },
      { ticker: 'Healthcare', name: '6M SDAC GSK LN + AMGN US + BAYN GY| 100% Strike| 70%AKI| 54% p.a.', return: 0.27, isBad: false, badWarning: '' },
    ],
    bottom3Comment: 'Bullish Sharkfin suffers partial principal loss as underlying index ends below initial level, but downside loss is floored at the MRN level.',
    bottom3: [
      { ticker: 'US', name: '6M Bullish Sharkfin on NDX, 100% Strike, 150% KO, 93.8%MRN ', return: -0.062000000000000055, isBad: true, badWarning: 'Suffer partial principal loss as Index ends below initial level, but downside loss floored at the MRN level.' },
      { ticker: 'Europe', name: '6M Bullish Sharkfin on SX5E, 100% Strike, 120% KO, 97.49%MRN ', return: -0.02510000000000001, isBad: false, badWarning: '' },
      { ticker: 'US', name: '6M Bullish Sharkfin on SPX, 100% Strike, 115% KO, 97.6%MRN ', return: -0.02400000000000002, isBad: false, badWarning: '' },
    ],
  },
  round2: {
    top3: [
      { ticker: 'US', name: '3M Put on Nasdaq 100, 100% Strike, 4.35% premium', return: 7.31264367816092, isBad: false, badWarning: '' },
      { ticker: 'Europe', name: '3M Put on Euro Stoxx 50, 100% Strike, 3.67% premium', return: 5.0817438692098085, isBad: false, badWarning: '' },
      { ticker: 'China', name: '3M Put on HangSeng China Enterprise, 100% Strike, 4.69% premium', return: 4.458422174840086, isBad: false, badWarning: '' },
    ],
    bottom3Comment: 'Booster underperforms when market drops and behave like direct investments.',
    bottom3: [
      { ticker: 'US Tech', name: '3M Booster NVDA US + TSLA US + AMZN US, 100% Strike, 490% PR', return: -0.5853, isBad: true, badWarning: 'Growth structure underperform when market corrects. MRN protect downside loss while provide upside capture in recovery phase.' },
      { ticker: 'Industrial', name: '3M Booster FDX US + SU FP + SIE GY, 100% Strike, 440% PR', return: -0.4222, isBad: false, badWarning: '' },
      { ticker: 'EU Consumer', name: '3M Booster ADS GY + MC FP + RMS FP, 100% Strike, 270% PR', return: -0.4135, isBad: false, badWarning: '' },
    ],
  },
  round3: {
    top3: [
      { ticker: 'US Tech', name: '3M Booster NVDA US + TSLA US + AMZN US, 100% Strike, 490% PR', return: 3.5496, isBad: false, badWarning: '' },
      { ticker: 'Industrial', name: '3M Booster FDX US + SU FP + SIE GY, 100% Strike, 440% PR', return: 1.54, isBad: false, badWarning: '' },
      { ticker: 'Mining', name: '3M Booster LIN US + RIO LN + AAL LN, 100% Strike, 475% PR', return: 1.0921, isBad: false, badWarning: '' },
    ],
    bottom3Comment: 'Hedging costs you money in calm, recovering markets but provide portfolio insurance and peaceful nights.',
    bottom3: [
      { ticker: 'Japan', name: '3M Put on Nikkei 225, 100% Strike, 5.91% premium', return: -1, isBad: true, badWarning: 'Hedging structures cost you money in calm, recovering markets but provide portfolio insurance and peaceful nights.' },
      { ticker: 'China', name: '3M Put on HangSeng China Enterprise, 100% Strike, 4.69% premium', return: -1, isBad: false, badWarning: '' },
      { ticker: 'US', name: '3M Put on Nasdaq 100, 100% Strike, 4.35% premium', return: -1, isBad: false, badWarning: '' },
    ],
  },
};

const STRUCTURED_PRODUCTS_COMMENTARIES = {
  "China Tech": [
    {
      "round": 1,
      "commentary": "The FCN provides income return and the low strike provided protection despite underlyings drifted lower",
      "return": 0.08
    },
    {
      "round": 2,
      "commentary": "FCN suffers MTM loss",
      "return": -0.3359
    },
    {
      "round": 3,
      "commentary": "FCN returns 100% principal back despite interim volatility in Round 2",
      "return": 0.6262611052552327
    }
  ],
  "US Tech1": [
    {
      "round": 1,
      "commentary": "The FCN provides income return and the low strike provided protection despite underlyings drifted lower",
      "return": 0.125
    },
    {
      "round": 2,
      "commentary": "FCN suffers MTM loss",
      "return": -0.2793
    },
    {
      "round": 3,
      "commentary": "FCN returns 100% principal back despite interim volatility in Round 2",
      "return": 0.5609823782433745
    }
  ],
  "US Tech3": [
    {
      "round": 2,
      "commentary": "V-BRC suffers MTM loss",
      "return": -0.3375
    },
    {
      "round": 3,
      "commentary": "V-BRC captures additional upside from the strong rebound post 80% KI trigger",
      "return": 1.8220377358490567
    }
  ],
  "Industrial1": [
    {
      "round": 2,
      "commentary": "SDAC suffers MTM loss",
      "return": -0.2356
    },
    {
      "round": 3,
      "commentary": "SDAC returns 100% principal + high coupon despite interim volatility in Round 2",
      "return": 0.5109890109890112
    }
  ],
  "Healthcare": [
    {
      "round": 2,
      "commentary": "SDAC suffers MTM loss",
      "return": -0.2217
    },
    {
      "round": 3,
      "commentary": "SDAC returns 100% principal + high coupon despite interim volatility in Round 2",
      "return": 0.6317615315431069
    }
  ],
  "US Consumer": [
    {
      "round": 2,
      "commentary": "SDAC suffers MTM loss",
      "return": -0.2504
    },
    {
      "round": 3,
      "commentary": "SDAC returns 100% principal + high coupon despite interim volatility in Round 2",
      "return": 0.4541088580576307
    }
  ],
  "EU Financial": [
    {
      "round": 2,
      "commentary": "DRA suffers MTM loss",
      "return": -0.1888
    },
    {
      "round": 3,
      "commentary": "DRA returns 100% principal back despite interim volatility in Round 2",
      "return": 0.3560157790927023
    }
  ],
  "US Financial": [
    {
      "round": 2,
      "commentary": "DRA suffers MTM loss",
      "return": -0.2311
    },
    {
      "round": 3,
      "commentary": "DRA returns 100% principal back despite interim volatility in Round 2",
      "return": 0.3948497854077253
    }
  ],
  "SG Banks": [
    {
      "round": 2,
      "commentary": "DRA suffers MTM loss",
      "return": 0
    },
    {
      "round": 3,
      "commentary": "DRA returns 100% principal back despite interim volatility in Round 2",
      "return": 0.05499999999999994
    }
  ],
  "Energy1": [
    {
      "round": 2,
      "commentary": "DCN suffers MTM loss",
      "return": -0.0765
    },
    {
      "round": 3,
      "commentary": "DCN returns 100% principal + digital coupon despite interim volatility in Round 2",
      "return": 0.34271792095289655
    }
  ],
  "Mining1": [
    {
      "round": 2,
      "commentary": "DCN suffers MTM loss",
      "return": -0.142
    },
    {
      "round": 3,
      "commentary": "DCN returns 100% principal + digital coupon despite interim volatility in Round 2",
      "return": 0.6899766899766899
    }
  ],
  "Utility": [
    {
      "round": 2,
      "commentary": "DCN suffers MTM loss",
      "return": 0.34
    },
    {
      "round": 3,
      "commentary": "DCN returns 100% principal + digital coupon despite interim volatility in Round 2",
      "return": 0
    }
  ],
  "3M Term": [
    {
      "round": 2,
      "commentary": "Cash alternative",
      "return": 0.00875
    }
  ],
  "6M Term": [
    {
      "round": 2,
      "commentary": "Cash alternative",
      "return": 0.008
    }
  ],
  "CRAN": [
    {
      "round": 2,
      "commentary": "Cash alternative",
      "return": 0.0125
    }
  ],
  "BLNUST10Y": [
    {
      "round": 3,
      "commentary": "BLN shortens duration and provide reinvestment opportunities",
      "return": 0.013076733283987174
    }
  ],
  "BLNUST20Y": [
    {
      "round": 3,
      "commentary": "BLN shortens duration and provide reinvestment opportunities",
      "return": 0.01356350184956856
    }
  ],
  "BLNUST30Y": [
    {
      "round": 3,
      "commentary": "BLN shortens duration and provide reinvestment opportunities",
      "return": 0.014778325123152802
    }
  ],
  "BLNJPM34": [
    {
      "round": 3,
      "commentary": "BLN shortens duration and provide reinvestment opportunities",
      "return": 0.015457320074825365
    }
  ],
  "BLNUBS29": [
    {
      "round": 3,
      "commentary": "BLN shortens duration and provide reinvestment opportunities",
      "return": 0.01584489715579185
    }
  ],
  "BLNHSBC36": [
    {
      "round": 3,
      "commentary": "BLN shortens duration and provide reinvestment opportunities",
      "return": 0.015651146766413992
    }
  ],
  "Energy23m": [
    {
      "round": 2,
      "commentary": "BEN behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.1287
    },
    {
      "round": 3,
      "commentary": "BEN outperformed the underlying asset by delivering convex upside with the high bonus coupon",
      "return": 0.26
    }
  ],
  "Biotech3m": [
    {
      "round": 2,
      "commentary": "BEN behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.3217
    },
    {
      "round": 3,
      "commentary": "BEN outperformed the underlying asset by delivering convex upside with the high bonus coupon",
      "return": 0.5
    }
  ],
  "Energy2": [
    {
      "round": 2,
      "commentary": "BEN behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.1287
    },
    {
      "round": 3,
      "commentary": "BEN outperformed direct investment with the convex bonus coupon payout despite interim volatility in Round 2",
      "return": 0.6067944450820613
    }
  ],
  "Biotech": [
    {
      "round": 2,
      "commentary": "Lookback BEN captures the lowest closing price during the first 2mths as entry price and cushion part of the MTM loss",
      "return": -0.1217
    },
    {
      "round": 3,
      "commentary": "Lookback BEN captures the lowest closing price during the first 2mths as entry price and outperformed direct investment with the convex bonus coupon payout",
      "return": 0.37470112717750204
    }
  ],
  "EU Consumer": [
    {
      "round": 2,
      "commentary": "Booster behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.4135
    },
    {
      "round": 3,
      "commentary": "Booster outperformed the underlying asset by delivering convex upside with the high PR",
      "return": 0.8551000000000001
    }
  ],
  "Financial2": [
    {
      "round": 2,
      "commentary": "Booster behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.2888
    },
    {
      "round": 3,
      "commentary": "Booster outperformed the underlying asset by delivering convex upside with the high PR",
      "return": 0.6520999999999999
    }
  ],
  "Industrial2": [
    {
      "round": 2,
      "commentary": "Booster behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.4222
    },
    {
      "round": 3,
      "commentary": "Booster outperformed the underlying asset by delivering convex upside with the high PR",
      "return": 1.54
    }
  ],
  "Mining2": [
    {
      "round": 2,
      "commentary": "Booster behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.2215
    },
    {
      "round": 3,
      "commentary": "Booster outperformed the underlying asset by delivering convex upside with the high PR",
      "return": 1.0921
    }
  ],
  "US Tech2": [
    {
      "round": 2,
      "commentary": "Booster behaves like direct investment in the worst-performing stock in the basket",
      "return": -0.5853
    },
    {
      "round": 3,
      "commentary": "Booster outperformed the underlying asset by delivering convex upside with the high PR",
      "return": 3.5496
    }
  ],
  "SPX 6m Put": [
    {
      "round": 1,
      "commentary": "Put provided full downside protection",
      "return": 2.997920997920998
    },
    {
      "round": 2,
      "commentary": "Put - positive MTM during sell-off",
      "return": 0.5582120582120583
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -0.389107538358906
    }
  ],
  "NDX 6m Put": [
    {
      "round": 1,
      "commentary": "Put provided full downside protection",
      "return": 7.979133226324238
    },
    {
      "round": 2,
      "commentary": "Put - positive MTM during sell-off",
      "return": 1.9020866773675762
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -0.6768876659292036
    }
  ],
  "SX5E 6m Put": [
    {
      "round": 1,
      "commentary": "Put provided full downside protection",
      "return": 1.6774774774774772
    },
    {
      "round": 2,
      "commentary": "Put - positive MTM during sell-off",
      "return": 1.010810810810811
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -0.5302889784946236
    }
  ],
  "HSCEI 6m Put": [
    {
      "round": 1,
      "commentary": "Put provided full downside protection",
      "return": -0.1794871794871795
    },
    {
      "round": 2,
      "commentary": "Put - positive MTM during sell-off",
      "return": 0.727395411605938
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -0.463990703125
    }
  ],
  "NKY 6m Put": [
    {
      "round": 1,
      "commentary": "Put provided full downside protection",
      "return": 1.1867167919799495
    },
    {
      "round": 2,
      "commentary": "Put - positive MTM during sell-off",
      "return": 0.5432330827067666
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -0.40371936662606567
    }
  ],
  "SPX 3m Put": [
    {
      "round": 1,
      "commentary": "100000",
      "return": 0
    },
    {
      "round": 2,
      "commentary": "Put participates on full downside",
      "return": 3.6408668730650153
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -1
    }
  ],
  "NDX 3m Put": [
    {
      "round": 2,
      "commentary": "Put participates on full downside",
      "return": 7.31264367816092
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -1
    }
  ],
  "SX5E 3m Put": [
    {
      "round": 2,
      "commentary": "Put participates on full downside",
      "return": 5.0817438692098085
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -1
    }
  ],
  "HSCEI 3m Put": [
    {
      "round": 2,
      "commentary": "Put participates on full downside",
      "return": 4.458422174840086
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -1
    }
  ],
  "NKY 3m Put": [
    {
      "round": 2,
      "commentary": "Put participates on full downside",
      "return": 3.1675126903553297
    },
    {
      "round": 3,
      "commentary": "Put expires worthless, max loss is premium paid",
      "return": -1
    }
  ],
  "China": [
    {
      "round": 2,
      "commentary": "The MRN Booster floored the max loss to 10%",
      "return": -0.1
    }
  ],
  "Gold": [
    {
      "round": 3,
      "commentary": "",
      "return": -0.07672421752377434
    }
  ],
  "SPX 3m BullShark": [
    {
      "round": 3,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0.07289999999999996
    }
  ],
  "NDX 3m BullShark": [
    {
      "round": 3,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0.3027999999999999
    }
  ],
  "SX5E 3m BullShark": [
    {
      "round": 3,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0.12900000000000003
    }
  ],
  "HSCEI 3m BullShark": [
    {
      "round": 3,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0.008599999999999986
    }
  ],
  "NKY 3m BullShark": [
    {
      "round": 3,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0.06359999999999999
    }
  ],
  "SPX 6m BullShark": [
    {
      "round": 2,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0
    }
  ],
  "NDX 6m BullShark": [
    {
      "round": 2,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0
    }
  ],
  "SX5E 6m BullShark": [
    {
      "round": 2,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0
    }
  ],
  "HSCEI 6m BullShark": [
    {
      "round": 2,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0
    }
  ],
  "NKY 6m BullShark": [
    {
      "round": 2,
      "commentary": "Bullish Sharkfin floored the max loss to 1-MRN level",
      "return": 0
    }
  ],
  "SPX 3m BearShark": [
    {
      "round": 3,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": -0.017100000000000004
    }
  ],
  "NDX 3m BearShark": [
    {
      "round": 3,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": -0.02959999999999996
    }
  ],
  "SX5E 3m BearShark": [
    {
      "round": 3,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": -0.02400000000000002
    }
  ],
  "HSCEI 3m BearShark": [
    {
      "round": 3,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": -0.03069999999999995
    }
  ],
  "NKY 3m BearShark": [
    {
      "round": 3,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": -0.032099999999999906
    }
  ],
  "SPX 6m BearShark": [
    {
      "round": 2,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": 0
    }
  ],
  "NDX 6m BearShark": [
    {
      "round": 2,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": 0
    }
  ],
  "SX5E 6m BearShark": [
    {
      "round": 2,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": 0
    }
  ],
  "HSCEI 6m BearShark": [
    {
      "round": 2,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": 0
    }
  ],
  "NKY 6m BearShark": [
    {
      "round": 2,
      "commentary": "Bearish Sharkfin participates on the full downside under no Barrier Event",
      "return": 0
    }
  ]
};


const OPTIMAL_SWITCHES = {
  round1: {
    'NVDA US': { ticker: 'US Tech1', category: 'income' },
    'MU US': { ticker: 'US Tech1', category: 'income' },
    '9988 HK': { ticker: 'China Tech', category: 'income' },
    '9888 HK': { ticker: 'China Tech', category: 'income' },
    '700 HK': { ticker: 'China Tech', category: 'income' },
    'C US': { ticker: 'US Financial', category: 'income' },
    'GS US': { ticker: 'US Financial', category: 'income' },
    'JPM US': { ticker: 'US Financial', category: 'income' },
    'DBS SP': { ticker: 'SG Banks', category: 'income' },
    'OCBC SP': { ticker: 'SG Banks', category: 'income' },
    'UOB SP': { ticker: 'SG Banks', category: 'income' },
    'BNP FP': { ticker: 'EU Financial', category: 'income' },
    'DBK GY': { ticker: 'EU Financial', category: 'income' },
    'UBSG SW': { ticker: 'EU Financial', category: 'income' },
    'AMGN US': { ticker: 'Healthcare', category: 'income' },
    'BAYN GY': { ticker: 'Healthcare', category: 'income' },
    'GSK LN': { ticker: 'Healthcare', category: 'income' },
    'CAT US': { ticker: 'Industrial1', category: 'income' },
    'HON US': { ticker: 'Industrial1', category: 'income' },
    'SIE GY': { ticker: 'Industrial1', category: 'income' },
    'NEM US': { ticker: 'Mining1', category: 'income' },
    'RIO LN': { ticker: 'Mining1', category: 'income' },
    'CVX US': { ticker: 'Energy1', category: 'income' },
    'OXY US': { ticker: 'Energy1', category: 'income' },
    'SBUX US': { ticker: 'US Consumer', category: 'income' },
    'COST US': { ticker: 'US Consumer', category: 'income' },
    'WMT US': { ticker: 'US Consumer', category: 'income' },
    'RWE GY': { ticker: 'Utility', category: 'income' },
    'VIE FP': { ticker: 'Utility', category: 'income' },
    'US Bill 3m': { ticker: '3M Term', category: 'income' },
    'US Bill 6m': { ticker: '6M Term', category: 'income' },
    'UST5Y': { ticker: 'CRAN', category: 'income' },
    'UST10Y': { ticker: 'BLNUST10Y', category: 'income' },
    'UST20Y': { ticker: 'BLNUST20Y', category: 'income' },
    'UST30Y': { ticker: 'BLNUST30Y', category: 'income' },
    'JPM34': { ticker: 'BLNJPM34', category: 'income' },
    'UBS29': { ticker: 'BLNUBS29', category: 'income' },
    'HSBC36': { ticker: 'BLNHSBC36', category: 'income' },
  },
  round2: {
    'NVDA US': { ticker: 'US Tech1', category: 'income' },
    'MU US': { ticker: 'US Tech1', category: 'income' },
    '9988 HK': { ticker: 'China Tech', category: 'income' },
    '9888 HK': { ticker: 'China Tech', category: 'income' },
    '700 HK': { ticker: 'China Tech', category: 'income' },
    'C US': { ticker: 'US Financial', category: 'income' },
    'GS US': { ticker: 'US Financial', category: 'income' },
    'JPM US': { ticker: 'US Financial', category: 'income' },
    'DBS SP': { ticker: 'SG Banks', category: 'income' },
    'OCBC SP': { ticker: 'SG Banks', category: 'income' },
    'UOB SP': { ticker: 'SG Banks', category: 'income' },
    'BNP FP': { ticker: 'EU Financial', category: 'income' },
    'DBK GY': { ticker: 'EU Financial', category: 'income' },
    'UBSG SW': { ticker: 'EU Financial', category: 'income' },
    'AMGN US': { ticker: 'Healthcare', category: 'income' },
    'BAYN GY': { ticker: 'Healthcare', category: 'income' },
    'GSK LN': { ticker: 'Healthcare', category: 'income' },
    'CAT US': { ticker: 'Industrial1', category: 'income' },
    'HON US': { ticker: 'Industrial1', category: 'income' },
    'SIE GY': { ticker: 'Industrial1', category: 'income' },
    'NEM US': { ticker: 'Mining1', category: 'income' },
    'RIO LN': { ticker: 'Mining1', category: 'income' },
    'CVX US': { ticker: 'Energy1', category: 'income' },
    'OXY US': { ticker: 'Energy1', category: 'income' },
    'SBUX US': { ticker: 'US Consumer', category: 'income' },
    'COST US': { ticker: 'US Consumer', category: 'income' },
    'WMT US': { ticker: 'US Consumer', category: 'income' },
    'RWE GY': { ticker: 'Utility', category: 'income' },
    'VIE FP': { ticker: 'Utility', category: 'income' },
    'US Bill 3m': { ticker: '3M Term', category: 'income' },
    'US Bill 6m': { ticker: '6M Term', category: 'income' },
    'UST5Y': { ticker: 'CRAN', category: 'income' },
    'UST10Y': { ticker: 'BLNUST10Y', category: 'income' },
    'UST20Y': { ticker: 'BLNUST20Y', category: 'income' },
    'UST30Y': { ticker: 'BLNUST30Y', category: 'income' },
    'JPM34': { ticker: 'BLNJPM34', category: 'income' },
    'UBS29': { ticker: 'BLNUBS29', category: 'income' },
    'HSBC36': { ticker: 'BLNHSBC36', category: 'income' },
  },
  round3: {
    'NVDA US': { ticker: 'US Tech2', category: 'growth' },
    'TSLA US': { ticker: 'US Tech2', category: 'growth' },
    'AMZN US': { ticker: 'US Tech2', category: 'growth' },
    'AAPL US': { ticker: 'Tech Tracker', category: 'growth' },
    '700 HK': { ticker: 'Tech Tracker', category: 'growth' },
    '9988 HK': { ticker: 'Tech Tracker', category: 'growth' },
    '700 HK': { ticker: 'Tech Tracker', category: 'growth' },
    'GOOGL US': { ticker: 'Tech Tracker', category: 'growth' },
    'MSFT US': { ticker: 'Tech Tracker', category: 'growth' },
    'META US': { ticker: 'Tech Tracker', category: 'growth' },
    'MU US': { ticker: 'Tech Tracker', category: 'growth' },
    'AZN LN': { ticker: 'Healthcare1', category: 'income' },
    'JNJ US': { ticker: 'Healthcare1', category: 'income' },
    'LLY US': { ticker: 'Healthcare1', category: 'income' },
    'PFE US': { ticker: 'Healthcare1', category: 'income' },
    'AMGN US': { ticker: 'Healthcare1', category: 'income' },
    'GSK LN': { ticker: 'Healthcare1', category: 'income' },
    'NOVOB DC': { ticker: 'Biotech3m', category: 'growth' },
    'BAYN GY': { ticker: 'Biotech3m', category: 'growth' },
    '7203 JP': { ticker: 'Consumer Tracker', category: 'growth' },
    'NKE US': { ticker: 'Consumer Tracker', category: 'growth' },
    'CFR SW': { ticker: 'Consumer Tracker', category: 'growth' },
    'MCD US': { ticker: 'Consumer Tracker', category: 'growth' },
    'CA FP': { ticker: 'Consumer Tracker', category: 'growth' },
    'CL US': { ticker: 'Consumer Tracker', category: 'growth' },
    'PG US': { ticker: 'Consumer Tracker', category: 'growth' },
    'ADS GY': { ticker: 'EU Consumer', category: 'growth' },
    'MC FP': { ticker: 'EU Consumer', category: 'growth' },
    'RMS FP': { ticker: 'EU Consumer', category: 'growth' },
    'SBUX US': { ticker: 'US Consumer3m', category: 'income' },
    'COST US': { ticker: 'US Consumer3m', category: 'income' },
    'WMT US': { ticker: 'US Consumer3m', category: 'income' },
    'CAT US': { ticker: 'Industrial3m', category: 'income' },
    'HON US': { ticker: 'Industrial3m', category: 'income' },
    'UPS US': { ticker: 'Industrial Tracker', category: 'growth' },
    'GE US': { ticker: 'Industrial Tracker', category: 'growth' },
    'AEM CN': { ticker: 'Mining Tracker', category: 'growth' },
    'AI FP': { ticker: 'Mining Tracker', category: 'growth' },
    'NEM US': { ticker: 'Mining13m', category: 'income' },
    'RIO LN': { ticker: 'Mining13m', category: 'income' },
    'LIN US': { ticker: 'Mining2', category: 'growth' },
    'RIO LN': { ticker: 'Mining2', category: 'growth' },
    'AAL LN': { ticker: 'Mining2', category: 'growth' },
    'CVX US': { ticker: 'Energy23m', category: 'growth' },
    'OXY US': { ticker: 'Energy23m', category: 'growth' },
    'SLB US': { ticker: 'Energy23m', category: 'growth' },
    'FDX US': { ticker: 'Industrial2', category: 'growth' },
    'SU FP': { ticker: 'Industrial2', category: 'growth' },
    'SIE GY': { ticker: 'Industrial2', category: 'growth' },
    'EMR US': { ticker: 'Industrial Tracker', category: 'growth' },
    '6501 JP': { ticker: 'Industrial Tracker', category: 'growth' },
    'BAS GY': { ticker: 'Mining Tracker', category: 'growth' },
    'DD US': { ticker: 'Mining Tracker', category: 'growth' },
    'BP/ LN': { ticker: 'Mining Tracker', category: 'growth' },
    'SHELL NA': { ticker: 'Mining Tracker', category: 'growth' },
    'XOM US': { ticker: 'Mining Tracker', category: 'growth' },
    'HAL US': { ticker: 'Mining Tracker', category: 'growth' },
    'C US': { ticker: 'Financial2', category: 'growth' },
    'GS US': { ticker: 'Financial2', category: 'growth' },
    'JPM US': { ticker: 'Financial2', category: 'growth' },
    'BNP FP': { ticker: 'Financial2', category: 'growth' },
    'DBK GY': { ticker: 'Financial2', category: 'growth' },
    'UBSG SW': { ticker: 'Financial2', category: 'growth' },
    'DBS SP': { ticker: 'Financial Tracker', category: 'growth' },
    'OCBC SP': { ticker: 'Financial Tracker', category: 'growth' },
    'UOB SP': { ticker: 'Financial Tracker', category: 'growth' },
    'RWE GY': { ticker: 'Utility3m', category: 'income' },
    'VIE FP': { ticker: 'Utility3m', category: 'income' },
    'US Bill 3m': { ticker: '3M Term', category: 'income' },
    'US Bill 6m': { ticker: '3M Term', category: 'income' },
    'UST5Y': { ticker: 'CRAN', category: 'income' },
  },
};


const InvestmentGame = () => {
  const [gameState, setGameState] = useState('setup');
  const [currentRound, setCurrentRound] = useState(0);
  
  // Compute initial cash as remaining allocation (1.0 - stocks - bonds - SPs)
  const getInitialCash = () => {
    let total = 0;
    Object.values(INITIAL_STOCKS).forEach(sector => {
      sector.forEach(stock => total += stock.allocation);
    });
    Object.values(INITIAL_BONDS).forEach(category => {
      category.forEach(bond => total += bond.allocation);
    });
    Object.values(STRUCTURED_PRODUCTS).forEach(category => {
      category.forEach(sp => total += sp.allocation);
    });
    return 1.0 - total;
  };
  
  const [portfolio, setPortfolio] = useState({
    stocks: JSON.parse(JSON.stringify(INITIAL_STOCKS)),
    bonds: JSON.parse(JSON.stringify(INITIAL_BONDS)),
    structuredProducts: JSON.parse(JSON.stringify(STRUCTURED_PRODUCTS)),
    cash: getInitialCash()
  });
  const [expandedAssetClasses, setExpandedAssetClasses] = useState({});
  const [expandedSectors, setExpandedSectors] = useState({});
  const [editingAssetClass, setEditingAssetClass] = useState(null);
  const [editingSector, setEditingSector] = useState(null);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [roundHistory, setRoundHistory] = useState([]);
  const [expandedClientAssets, setExpandedClientAssets] = useState({});
  const [expandedOptimalAssets, setExpandedOptimalAssets] = useState({});
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [hasPortfolioChanged, setHasPortfolioChanged] = useState(false);

  const TOTAL_VALUE = 1000000;
  const EPSILON = 0.00001; // Tolerance for floating point precision errors (~$10)
  
  // Round passwords
  const ROUND_PASSWORDS = {
    round2: '1823',
    round3: '5648',
    restart: '1312'
  };

  const calculatePortfolioValue = (port = portfolio) => {
    let equityValue = 0;
    Object.values(port.stocks).forEach(sector => {
      sector.forEach(stock => equityValue += stock.allocation);
    });

    let bondValue = 0;
    Object.values(port.bonds).forEach(category => {
      category.forEach(bond => bondValue += bond.allocation);
    });

    let spValue = 0;
    Object.values(port.structuredProducts).forEach(category => {
      category.forEach(sp => spValue += sp.allocation);
    });

    const actualTotal = (equityValue + bondValue + spValue + port.cash) * TOTAL_VALUE;

    return {
      equity: equityValue * TOTAL_VALUE,
      bonds: bondValue * TOTAL_VALUE,
      structuredProducts: spValue * TOTAL_VALUE,
      cash: port.cash * TOTAL_VALUE,
      total: actualTotal  // Return actual total instead of hardcoded TOTAL_VALUE
    };
  };

  const applyMarketReturns = (roundNum) => {
    const scenario = MARKET_SCENARIOS[`round${roundNum}`];
    const roundKey = `round${roundNum}`;
    let newPortfolio = JSON.parse(JSON.stringify(portfolio));
    
    Object.keys(newPortfolio.stocks).forEach(sector => {
      newPortfolio.stocks[sector].forEach(stock => {
        const ret = STOCK_RETURNS[sector]?.[roundKey]?.[stock.ticker] ?? 0;
        stock.allocation *= (1 + ret);
      });
    });

    Object.keys(newPortfolio.bonds).forEach(category => {
      newPortfolio.bonds[category].forEach(bond => {
        const ret = BOND_RETURNS[category]?.[roundKey]?.[bond.ticker] ?? 0;
        bond.allocation *= (1 + ret);
      });
    });

    Object.keys(newPortfolio.structuredProducts).forEach(category => {
      newPortfolio.structuredProducts[category].forEach(sp => {
        const ret = SP_RETURNS[roundKey]?.[sp.ticker] ?? 0;
        sp.allocation *= (1 + ret);
      });
    });

    newPortfolio.cash *= (1 + scenario.cash);

    return newPortfolio;
  };

  const generateOptimalPortfolio = (roundNum, clientPortfolio) => {
  const values = calculatePortfolioValue(clientPortfolio);
  const scenario = MARKET_SCENARIOS[`round${roundNum}`];
  const alternatives = OPTIMAL_SWITCHES[`round${roundNum}`];
  
  // Create optimal portfolio starting from client's current portfolio
  const optimal = JSON.parse(JSON.stringify(clientPortfolio));
  const MIN_ALLOCATION = 0.05; // 5% minimum
  
  // Track switches for commentary
  const switchDetails = [];
  
  // Helper: Get individual instrument return by ticker
  const getInstrumentReturn = (type, sectorOrCategory, ticker) => {
    const roundKey = `round${roundNum}`;
    if (type === 'stock') return STOCK_RETURNS[sectorOrCategory]?.[roundKey]?.[ticker] ?? 0;
    if (type === 'bond') return BOND_RETURNS[sectorOrCategory]?.[roundKey]?.[ticker] ?? 0;
    return 0;
  };
  
  // Helper: Get SP alternative return by ticker
  const getSPAlternativeReturn = (alternativeTicker) => {
    const roundKey = `round${roundNum}`;
    return SP_RETURNS[roundKey]?.[alternativeTicker] ?? 0;
  };
  
  // ROUND 2 SPECIAL: Buy hedge FIRST before any switching
  if (roundNum === 2) {
    // Calculate total stock value
    let totalStockValue = 0;
    Object.values(optimal.stocks).forEach(sector => {
      sector.forEach(stock => {
        totalStockValue += stock.allocation;
      });
    });
    
    // Calculate existing hedge
    let existingHedge = 0;
    optimal.structuredProducts.hedging.forEach(hedge => {
      existingHedge += hedge.allocation;
    });
    
    // Calculate hedge cost for each instrument based on its own premium
    // Split stock protection equally among all hedge instruments
    const hedgeCount = optimal.structuredProducts.hedging.length;
    const stockValuePerHedge = totalStockValue / hedgeCount;
    
    let totalHedgeCost = 0;
    const hedgeCosts = [];
    
    optimal.structuredProducts.hedging.forEach(hedge => {
      // Cost = premium × stock value this hedge protects
      const cost = hedge.premium * stockValuePerHedge;
      hedgeCosts.push({ hedge, cost });
      totalHedgeCost += cost;
    });
    
    const hedgeNeeded = totalHedgeCost - existingHedge;
    
    if (hedgeNeeded > 0) {
      // Add hedge based on each instrument's cost (not split equally)
      hedgeCosts.forEach(({ hedge, cost }) => {
        hedge.allocation += cost;
      });
      optimal.cash -= totalHedgeCost;
      
      switchDetails.push({
        type: 'hedge_added',
        amount: totalHedgeCost,
        totalProtected: totalStockValue,
        premium: totalHedgeCost / totalStockValue // Effective premium
      });
    }
  }
  
  // Process STOCKS: Compare and switch if SP alternative is better
  Object.keys(optimal.stocks).forEach(sector => {
    optimal.stocks[sector].forEach((stock, idx) => {
      if (stock.allocation > 0 && alternatives[stock.ticker]) {
        const stockReturn = getInstrumentReturn('stock', sector, stock.ticker);
        const alt = alternatives[stock.ticker];
        const spReturn = getSPAlternativeReturn(alt.ticker);
        
        // Get SP name from STRUCTURED_PRODUCTS
        let spName = alt.ticker; // fallback to ticker
        if (STRUCTURED_PRODUCTS[alt.category]) {
          const sp = STRUCTURED_PRODUCTS[alt.category].find(p => p.ticker === alt.ticker);
          if (sp) {
            spName = sp.name;
          }
        }
        
        // Only switch if SP return is better
        if (spReturn > stockReturn) {
          let switchAmount;
          
          // Special logic for hedging (Round 2): Keep old 50%/100% logic
          if (alt.category === 'hedging') {
            switchAmount = stock.allocation * 0.5; // 50% default
            // If 50% < 5%, switch 100%
            if (switchAmount > 0 && switchAmount < MIN_ALLOCATION) {
              switchAmount = stock.allocation;
            }
          } else {
            // New logic for Income, CapPres, Growth
            const fiftyPercent = stock.allocation * 0.5;
            
            if (fiftyPercent >= MIN_ALLOCATION) {
              // Stock >= 10%: Switch 50%
              switchAmount = fiftyPercent;
            } else if (stock.allocation >= MIN_ALLOCATION) {
              // Stock 5-9.9%: Switch exactly 5%
              switchAmount = MIN_ALLOCATION;
            } else {
              // Stock < 5%: Switch all of it
              switchAmount = stock.allocation;
            }
          }
          
          
          // Perform the switch
          stock.allocation -= switchAmount;
          
          // Add to appropriate SP category
          const spCategory = optimal.structuredProducts[alt.category];
          const spIndex = spCategory.findIndex(sp => sp.ticker === alt.ticker);
          if (spIndex >= 0) {
            spCategory[spIndex].allocation += switchAmount;
          }
          
          switchDetails.push({
            type: 'stock_switch',
            from: stock.ticker,
            fromName: stock.name,
            to: alt.ticker,
            toName: spName,
            amount: switchAmount,
            stockReturn: stockReturn,
            spReturn: spReturn,
            category: alt.category
          });
        }
      }
    });
  });
  
  // Process BONDS: Compare and switch if SP alternative is better
  Object.keys(optimal.bonds).forEach(category => {
    optimal.bonds[category].forEach((bond, idx) => {
      if (bond.allocation > 0 && alternatives[bond.ticker]) {
        const bondReturn = getInstrumentReturn('bond', category, bond.ticker);
        const alt = alternatives[bond.ticker];
        const spReturn = getSPAlternativeReturn(alt.ticker);
        
        // Get SP name from STRUCTURED_PRODUCTS
        let spName = alt.ticker; // fallback to ticker
        if (STRUCTURED_PRODUCTS[alt.category]) {
          const sp = STRUCTURED_PRODUCTS[alt.category].find(p => p.ticker === alt.ticker);
          if (sp) {
            spName = sp.name;
          }
        }
        
        // Only switch if SP return is better
        if (spReturn > bondReturn) {
          let switchAmount;
          
          // New logic for all bond switches (bonds always go to Income, not hedging)
          const fiftyPercent = bond.allocation * 0.5;
          
          if (fiftyPercent >= MIN_ALLOCATION) {
            // Bond >= 10%: Switch 50%
            switchAmount = fiftyPercent;
          } else if (bond.allocation >= MIN_ALLOCATION) {
            // Bond 5-9.9%: Switch exactly 5%
            switchAmount = MIN_ALLOCATION;
          } else {
            // Bond < 5%: Switch all of it
            switchAmount = bond.allocation;
          }
          
          
          // Perform the switch
          bond.allocation -= switchAmount;
          
          // Add to appropriate SP category
          const spCategory = optimal.structuredProducts[alt.category];
          const spIndex = spCategory.findIndex(sp => sp.ticker === alt.ticker);
          if (spIndex >= 0) {
            spCategory[spIndex].allocation += switchAmount;
          }
          
          switchDetails.push({
            type: 'bond_switch',
            from: bond.ticker,
            fromName: bond.name,
            to: alt.ticker,
            toName: spName,
            amount: switchAmount,
            bondReturn: bondReturn,
            spReturn: spReturn,
            category: alt.category
          });
        }
      }
    });
  });
  
  // Check if user bought correct SPs
  const userSPSelections = [];
  const incorrectSPs = [];
  
  Object.keys(clientPortfolio.structuredProducts).forEach(category => {
    clientPortfolio.structuredProducts[category].forEach(sp => {
      if (sp.allocation > 0) {
        userSPSelections.push({
          ticker: sp.ticker,
          name: sp.name,
          category: category,
          allocation: sp.allocation
        });
        
        // Check if this SP is in the switch details (meaning it's correct)
        const isCorrect = switchDetails.some(s => s.to === sp.ticker);
        
        if (!isCorrect) {
          // Check if it's a bad SP for this round
          const badSPs = [...SP_PERFORMANCE_RANKINGS[`round${roundNum}`].bottom3];
          const badSP = badSPs.find(b => b.ticker === sp.ticker && b.isBad);
          
          if (badSP) {
            incorrectSPs.push({
              ticker: sp.ticker,
              name: sp.name,
              warning: badSP.badWarning
            });
          }
        }
      }
    });
  });
  
  // Deploy high cash in optimal portfolio (>$200K = 0.2 allocation)
  if (optimal.cash > 0.2) {
    const TOTAL_VALUE = 1000000;
    let cashToInvest = optimal.cash;
    const roundKey = `round${roundNum}`;
    
    // Helper to check if SP already has allocation in optimal portfolio
    const hasAllocation = (ticker, category) => {
      return optimal.structuredProducts[category]?.some(sp => sp.ticker === ticker && sp.allocation > 0);
    };
    
    // Helper to find SP index by ticker
    const findSPIndex = (ticker, category) => {
      return optimal.structuredProducts[category]?.findIndex(sp => sp.ticker === ticker);
    };
    
    // Get top 3 capital preservation SPs with positive returns
    const allCapPresSPs = [];
    STRUCTURED_PRODUCTS.capitalPreservation.forEach(sp => {
      const spReturn = SP_RETURNS[roundKey]?.[sp.ticker] ?? 0;
      if (spReturn > 0 && !hasAllocation(sp.ticker, 'capitalPreservation')) {
        allCapPresSPs.push({
          ...sp,
          return: spReturn
        });
      }
    });
    
    // Sort by return (highest first) and take top 3
    const top3CapPres = allCapPresSPs
      .sort((a, b) => b.return - a.return)
      .slice(0, 3);
    
    // Allocate $50K (0.05) to each top 3 capital preservation
    top3CapPres.forEach(sp => {
      if (cashToInvest >= 0.05) {
        const spIndex = findSPIndex(sp.ticker, 'capitalPreservation');
        if (spIndex !== -1) {
          optimal.structuredProducts.capitalPreservation[spIndex].allocation += 0.05;
          cashToInvest -= 0.05;
        }
      }
    });
    
    // Split remaining cash equally between CRAN and 3M Term
    if (cashToInvest > 0) {
      const hasCRAN = !hasAllocation('CRAN', 'income');
      const hasTerm = !hasAllocation('3M Term', 'income');
      
      if (hasCRAN && hasTerm) {
        // Both available: split 50/50
        const halfCash = cashToInvest / 2;
        
        const cranIndex = findSPIndex('CRAN', 'income');
        if (cranIndex !== -1) {
          optimal.structuredProducts.income[cranIndex].allocation += halfCash;
        }
        
        const termIndex = findSPIndex('3M Term', 'income');
        if (termIndex !== -1) {
          optimal.structuredProducts.income[termIndex].allocation += halfCash;
        }
        
        cashToInvest = 0;
      } else if (hasCRAN) {
        // Only CRAN available: put all in CRAN
        const cranIndex = findSPIndex('CRAN', 'income');
        if (cranIndex !== -1) {
          optimal.structuredProducts.income[cranIndex].allocation += cashToInvest;
          cashToInvest = 0;
        }
      } else if (hasTerm) {
        // Only 3M Term available: put all in 3M Term
        const termIndex = findSPIndex('3M Term', 'income');
        if (termIndex !== -1) {
          optimal.structuredProducts.income[termIndex].allocation += cashToInvest;
          cashToInvest = 0;
        }
      }
    }
    
    // Update optimal cash
    optimal.cash = cashToInvest;
  }
  
  return {
    portfolio: optimal,
    switchDetails: switchDetails,
    userSPSelections: userSPSelections,
    incorrectSPs: incorrectSPs,
    message: `Optimal switches for Round ${roundNum}`
  };
  };

  // Generate simplified additional recommendations (shows what was applied to optimal portfolio)
  const generateAdditionalRecommendations = (roundNum, optimalPortfolio, portfolioAfter, roundHistory) => {
    const recommendations = [];
    const TOTAL_VALUE = 1000000;
    const cashAmount = portfolioAfter.cash * TOTAL_VALUE;
    
    // Show high cash recommendations that were applied to optimal portfolio
    if (cashAmount > 200000) {
      const roundKey = `round${roundNum}`;
      
      // Check what was added to optimal portfolio
      // Compare client's portfolio vs optimal portfolio for new SP allocations
      
      const checkAdded = (category, ticker) => {
        const clientSP = portfolioAfter.structuredProducts[category]?.find(sp => sp.ticker === ticker);
        const optimalSP = optimalPortfolio.structuredProducts[category]?.find(sp => sp.ticker === ticker);
        
        const clientAlloc = clientSP?.allocation || 0;
        const optimalAlloc = optimalSP?.allocation || 0;
        
        // If optimal has more than client, it was added
        if (optimalAlloc > clientAlloc) {
          return {
            added: true,
            amount: (optimalAlloc - clientAlloc) * TOTAL_VALUE,
            name: optimalSP.name
          };
        }
        return { added: false };
      };
      
      // Check all capital preservation products with positive returns (matching optimal portfolio logic)
      const allCapPresSPs = [];
      STRUCTURED_PRODUCTS.capitalPreservation.forEach(sp => {
        const spReturn = SP_RETURNS[roundKey]?.[sp.ticker] ?? 0;
        if (spReturn > 0) {
          allCapPresSPs.push({
            ticker: sp.ticker,
            return: spReturn
          });
        }
      });
      
      // Sort by return and take top 3
      const top3CapPresTickers = allCapPresSPs
        .sort((a, b) => b.return - a.return)
        .slice(0, 3)
        .map(sp => sp.ticker);
      
      // Check which ones were added to optimal portfolio
      top3CapPresTickers.forEach(ticker => {
        const result = checkAdded('capitalPreservation', ticker);
        if (result.added) {
          recommendations.push({
            type: 'high_cash',
            action: 'add',
            ticker: ticker,
            name: result.name,
            amount: result.amount,
            reason: 'Capital preservation with attractive returns'
          });
        }
      });
      
      // Check CRAN
      const cranCheck = checkAdded('income', 'CRAN');
      if (cranCheck.added) {
        recommendations.push({
          type: 'high_cash',
          action: 'add',
          ticker: 'CRAN',
          name: cranCheck.name,
          amount: cranCheck.amount,
          reason: 'Long-term rates accrual note for stable income'
        });
      }
      
      // Check 3M Term
      const termCheck = checkAdded('income', '3M Term');
      if (termCheck.added) {
        recommendations.push({
          type: 'high_cash',
          action: 'add',
          ticker: '3M Term',
          name: termCheck.name,
          amount: termCheck.amount,
          reason: 'Short-term fixed income alternative to cash'
        });
      }
    }
    
    // Scenario 2: Round 3 Recovery Calculation (unchanged)
    if (roundNum === 3 && roundHistory.length >= 2) {
      const afterTotal = Object.values(portfolioAfter.stocks).flat().reduce((sum, s) => sum + s.allocation, 0) +
                         Object.values(portfolioAfter.bonds).flat().reduce((sum, b) => sum + b.allocation, 0) +
                         Object.values(portfolioAfter.structuredProducts).flat().reduce((sum, sp) => sum + sp.allocation, 0) +
                         portfolioAfter.cash;
      const afterTotalValue = afterTotal * TOTAL_VALUE;
      
      // Check if portfolio < $1M AND Round 2 loss > 30%
      if (afterTotalValue < 1000000) {
        const round2Before = roundHistory[1].portfolioBefore;
        const round2After = roundHistory[1].portfolioAfter;
        const r2BeforeTotal = Object.values(round2Before.stocks).flat().reduce((sum, s) => sum + s.allocation, 0) +
                             Object.values(round2Before.bonds).flat().reduce((sum, b) => sum + b.allocation, 0) +
                             Object.values(round2Before.structuredProducts).flat().reduce((sum, sp) => sum + sp.allocation, 0) +
                             round2Before.cash;
        const r2AfterTotal = Object.values(round2After.stocks).flat().reduce((sum, s) => sum + s.allocation, 0) +
                            Object.values(round2After.bonds).flat().reduce((sum, b) => sum + b.allocation, 0) +
                            Object.values(round2After.structuredProducts).flat().reduce((sum, sp) => sum + sp.allocation, 0) +
                            round2After.cash;
        const round2Loss = (r2AfterTotal - r2BeforeTotal) / r2BeforeTotal;
        
        if (round2Loss < -0.30) {
          const requiredGain = Math.abs(round2Loss) / (1 - Math.abs(round2Loss));
          recommendations.push({
            type: 'recovery_needed',
            loss: round2Loss,
            requiredGain: requiredGain,
            message: `You lost ${(Math.abs(round2Loss) * 100).toFixed(1)}% in Round 2. To recover, you need a ${(requiredGain * 100).toFixed(1)}% gain`
          });
        }
      }
    }
    
    return recommendations;
  };

  // Only flag SP minimum violations caused by the adjustment being made,
  // not pre-existing violations unrelated to the action (e.g. adjusting equities
  // should never block due to an SP that was already below minimum).
  const checkMinimumViolations = (testPortfolio, originalPortfolio) => {
    const violations = [];
    
    // Compare in dollar terms directly — sp.allocation is a fraction of TOTAL_VALUE ($1M),
    // so sp.allocation * TOTAL_VALUE gives the actual dollar amount.
    // Do NOT derive a minPercentage from the current portfolio total, because in Round 3
    // the portfolio value differs from $1M, which makes the percentage threshold wrong.
    const MIN_DOLLAR_AMOUNT = 50000;
    
    // Check structured products - EXCEPT hedging products (no minimum for hedging)
    Object.keys(testPortfolio.structuredProducts).forEach(category => {
      // Skip hedging category - no minimum for hedging products
      if (category === 'hedging') return;
      
      testPortfolio.structuredProducts[category].forEach((sp, idx) => {
        const spDollarValue = sp.allocation * TOTAL_VALUE;
        if (sp.allocation > 0 && spDollarValue < MIN_DOLLAR_AMOUNT) {
          // Only flag if this violation was caused by the adjustment —
          // i.e. the SP allocation actually decreased from the original.
          const originalAlloc = originalPortfolio?.structuredProducts[category]?.[idx]?.allocation ?? sp.allocation;
          if (sp.allocation < originalAlloc) {
            violations.push(sp.name);
          }
        }
      });
    });
    
    return violations;
  };

  const handleAssetClassAdjust = (assetClass, percentChange) => {
    const newPortfolio = JSON.parse(JSON.stringify(portfolio));
    const values = calculatePortfolioValue(newPortfolio);
    const adjustmentPercent = percentChange / 100;

    let currentTotal = 0;
    if (assetClass === 'equity') {
      currentTotal = values.equity / TOTAL_VALUE;
    } else if (assetClass === 'bonds') {
      currentTotal = values.bonds / TOTAL_VALUE;
    } else if (assetClass === 'sp') {
      currentTotal = values.structuredProducts / TOTAL_VALUE;
    }

    const newTotal = currentTotal + adjustmentPercent;

    if (newTotal < 0) {
      alert('Cannot reduce allocation below 0%');
      return;
    }

    if (adjustmentPercent > newPortfolio.cash + EPSILON) {
      alert('Insufficient cash');
      return;
    }

    if (assetClass === 'equity') {
      const scaleFactor = currentTotal > 0 ? newTotal / currentTotal : 1;
      Object.keys(newPortfolio.stocks).forEach(sector => {
        newPortfolio.stocks[sector].forEach(stock => {
          if (currentTotal > 0) {
            stock.allocation *= scaleFactor;
          } else {
            stock.allocation = adjustmentPercent / Object.values(newPortfolio.stocks).flat().length;
          }
        });
      });
    } else if (assetClass === 'bonds') {
      const scaleFactor = currentTotal > 0 ? newTotal / currentTotal : 1;
      Object.keys(newPortfolio.bonds).forEach(category => {
        newPortfolio.bonds[category].forEach(bond => {
          if (currentTotal > 0) {
            bond.allocation *= scaleFactor;
          } else {
            bond.allocation = adjustmentPercent / Object.values(newPortfolio.bonds).flat().length;
          }
        });
      });
    } else if (assetClass === 'sp') {
      const scaleFactor = currentTotal > 0 ? newTotal / currentTotal : 1;
      Object.keys(newPortfolio.structuredProducts).forEach(category => {
        newPortfolio.structuredProducts[category].forEach(sp => {
          if (currentTotal > 0) {
            sp.allocation *= scaleFactor;
          } else {
            sp.allocation = adjustmentPercent / Object.values(newPortfolio.structuredProducts).flat().length;
          }
        });
      });
    }

    // Check for minimum violations after scaling (excluding hedging products)
    const violations = checkMinimumViolations(newPortfolio, portfolio);
    if (violations.length > 0) {
      alert(`This adjustment would create positions below $50,000 minimum:\n\n${violations.join(', ')}\n\nPlease adjust individual positions or remove them entirely.\n\nNote: Hedging products have no minimum.`);
      return;
    }

    newPortfolio.cash -= adjustmentPercent;
    setPortfolio(newPortfolio);
  };

  const handleSectorAdjust = (assetClass, sector, percentChange) => {
    const adjustmentPercent = percentChange / 100;
    const newPortfolio = JSON.parse(JSON.stringify(portfolio));

    if (assetClass === 'stocks') {
      const currentSectorTotal = newPortfolio.stocks[sector].reduce((sum, stock) => sum + stock.allocation, 0);
      const newSectorTotal = currentSectorTotal + adjustmentPercent;
      
      if (newSectorTotal < 0) {
        alert('Cannot reduce allocation below 0%');
        return;
      }

      if (adjustmentPercent > newPortfolio.cash + EPSILON) {
        alert('Insufficient cash');
        return;
      }

      const scaleFactor = currentSectorTotal > 0 ? newSectorTotal / currentSectorTotal : 1;
      newPortfolio.stocks[sector].forEach(stock => {
        if (currentSectorTotal > 0) {
          stock.allocation *= scaleFactor;
        } else {
          stock.allocation = adjustmentPercent / newPortfolio.stocks[sector].length;
        }
      });

      newPortfolio.cash -= adjustmentPercent;
    } else if (assetClass === 'bonds') {
      const currentCategoryTotal = newPortfolio.bonds[sector].reduce((sum, bond) => sum + bond.allocation, 0);
      const newCategoryTotal = currentCategoryTotal + adjustmentPercent;
      
      if (newCategoryTotal < 0) {
        alert('Cannot reduce allocation below 0%');
        return;
      }

      if (adjustmentPercent > newPortfolio.cash + EPSILON) {
        alert('Insufficient cash');
        return;
      }

      const scaleFactor = currentCategoryTotal > 0 ? newCategoryTotal / currentCategoryTotal : 1;
      newPortfolio.bonds[sector].forEach(bond => {
        if (currentCategoryTotal > 0) {
          bond.allocation *= scaleFactor;
        } else {
          bond.allocation = adjustmentPercent / newPortfolio.bonds[sector].length;
        }
      });

      newPortfolio.cash -= adjustmentPercent;
    } else if (assetClass === 'sp') {
      const currentCategoryTotal = newPortfolio.structuredProducts[sector].reduce((sum, sp) => sum + sp.allocation, 0);
      const newCategoryTotal = currentCategoryTotal + adjustmentPercent;
      
      if (newCategoryTotal < 0) {
        alert('Cannot reduce allocation below 0%');
        return;
      }

      if (adjustmentPercent > newPortfolio.cash + EPSILON) {
        alert('Insufficient cash');
        return;
      }

      const scaleFactor = currentCategoryTotal > 0 ? newCategoryTotal / currentCategoryTotal : 1;
      newPortfolio.structuredProducts[sector].forEach(sp => {
        if (currentCategoryTotal > 0) {
          sp.allocation *= scaleFactor;
        } else {
          sp.allocation = adjustmentPercent / newPortfolio.structuredProducts[sector].length;
        }
      });

      newPortfolio.cash -= adjustmentPercent;
    }

    // Check for minimum violations after scaling (excluding hedging products)
    const violations = checkMinimumViolations(newPortfolio, portfolio);
    if (violations.length > 0) {
      alert(`This adjustment would create positions below $50,000 minimum:\n\n${violations.join(', ')}\n\nPlease adjust individual positions or remove them entirely.\n\nNote: Hedging products have no minimum.`);
      return;
    }

    setPortfolio(newPortfolio);
  };

  const handleInstrumentAdjust = (assetClass, sector, index, percentChange) => {
    const adjustmentPercent = percentChange / 100;
    const newPortfolio = JSON.parse(JSON.stringify(portfolio));

    if (assetClass === 'stocks') {
      const currentAllocation = newPortfolio.stocks[sector][index].allocation;
      const newAllocation = currentAllocation + adjustmentPercent;
      
      if (newAllocation < 0) {
        alert('Cannot reduce allocation below 0%');
        return;
      }

      // Stocks can be any amount - no minimum restriction

      if (adjustmentPercent > newPortfolio.cash + EPSILON) {
        alert('Insufficient cash');
        return;
      }

      newPortfolio.stocks[sector][index].allocation = newAllocation;
      newPortfolio.cash -= adjustmentPercent;
    } else if (assetClass === 'bonds') {
      const currentAllocation = newPortfolio.bonds[sector][index].allocation;
      const newAllocation = currentAllocation + adjustmentPercent;
      
      if (newAllocation < 0) {
        alert('Cannot reduce allocation below 0%');
        return;
      }

      // Bonds can be any amount - no minimum restriction

      if (adjustmentPercent > newPortfolio.cash + EPSILON) {
        alert('Insufficient cash');
        return;
      }

      newPortfolio.bonds[sector][index].allocation = newAllocation;
      newPortfolio.cash -= adjustmentPercent;
    } else if (assetClass === 'sp') {
      const currentAllocation = newPortfolio.structuredProducts[sector][index].allocation;
      const newAllocation = currentAllocation + adjustmentPercent;
      
      if (newAllocation < 0) {
        alert('Cannot reduce allocation below 0%');
        return;
      }

      // Calculate current portfolio value
      const portfolioValues = calculatePortfolioValue(newPortfolio);
      const currentPortfolioValue = portfolioValues.total;
      
      // Enforce $50,000 minimum for non-hedging SPs: must be 0 or >= $50,000
      // Hedging products have NO minimum requirement
      if (sector !== 'hedging') {
        const MIN_DOLLAR_AMOUNT = 50000;
        const minPercentage = MIN_DOLLAR_AMOUNT / currentPortfolioValue;
        
        if (newAllocation > 0 && newAllocation < minPercentage) {
          alert(`Minimum investment is $50,000 for this product. Position must be either 0 or at least $50,000.\n\nNote: Hedging products have no minimum requirement.`);
          return;
        }
      }

      if (adjustmentPercent > newPortfolio.cash + EPSILON) {
        alert('Insufficient cash');
        return;
      }

      newPortfolio.structuredProducts[sector][index].allocation = newAllocation;
      newPortfolio.cash -= adjustmentPercent;
    }

    setPortfolio(newPortfolio);
  };

  // Zero out a single instrument
  const zeroOutInstrument = (assetClass, sector, index) => {
    const newPortfolio = JSON.parse(JSON.stringify(portfolio));
    
    if (assetClass === 'stocks') {
      const currentAllocation = newPortfolio.stocks[sector][index].allocation;
      newPortfolio.stocks[sector][index].allocation = 0;
      newPortfolio.cash += currentAllocation;
    } else if (assetClass === 'bonds') {
      const currentAllocation = newPortfolio.bonds[sector][index].allocation;
      newPortfolio.bonds[sector][index].allocation = 0;
      newPortfolio.cash += currentAllocation;
    } else if (assetClass === 'sp') {
      const currentAllocation = newPortfolio.structuredProducts[sector][index].allocation;
      newPortfolio.structuredProducts[sector][index].allocation = 0;
      newPortfolio.cash += currentAllocation;
    }
    
    setPortfolio(newPortfolio);
  };

  // Zero out an entire sector
  const zeroOutSector = (assetClass, sector) => {
    const newPortfolio = JSON.parse(JSON.stringify(portfolio));
    
    if (assetClass === 'stocks') {
      newPortfolio.stocks[sector].forEach(stock => {
        newPortfolio.cash += stock.allocation;
        stock.allocation = 0;
      });
    } else if (assetClass === 'bonds') {
      newPortfolio.bonds[sector].forEach(bond => {
        newPortfolio.cash += bond.allocation;
        bond.allocation = 0;
      });
    } else if (assetClass === 'sp') {
      newPortfolio.structuredProducts[sector].forEach(sp => {
        newPortfolio.cash += sp.allocation;
        sp.allocation = 0;
      });
    }
    
    setPortfolio(newPortfolio);
  };

  // Zero out an entire asset class
  const zeroOutAssetClass = (assetClass) => {
    const newPortfolio = JSON.parse(JSON.stringify(portfolio));
    
    if (assetClass === 'equity') {
      Object.keys(newPortfolio.stocks).forEach(sector => {
        newPortfolio.stocks[sector].forEach(stock => {
          newPortfolio.cash += stock.allocation;
          stock.allocation = 0;
        });
      });
    } else if (assetClass === 'bonds') {
      Object.keys(newPortfolio.bonds).forEach(category => {
        newPortfolio.bonds[category].forEach(bond => {
          newPortfolio.cash += bond.allocation;
          bond.allocation = 0;
        });
      });
    } else if (assetClass === 'sp') {
      Object.keys(newPortfolio.structuredProducts).forEach(category => {
        newPortfolio.structuredProducts[category].forEach(sp => {
          newPortfolio.cash += sp.allocation;
          sp.allocation = 0;
        });
      });
    }
    
    setPortfolio(newPortfolio);
  };

  const checkIfPortfolioChanged = () => {
    const initial = {
      stocks: INITIAL_STOCKS,
      bonds: INITIAL_BONDS,
      structuredProducts: STRUCTURED_PRODUCTS,
      cash: getInitialCash()
    };
    
    // Compare current portfolio with initial
    const currentStr = JSON.stringify(portfolio);
    const initialStr = JSON.stringify(initial);
    
    return currentStr !== initialStr;
  };

  const handleSubmitClick = () => {
    const changed = checkIfPortfolioChanged();
    setHasPortfolioChanged(changed);
    setShowSubmitConfirmation(true);
  };

  const submitRound = () => {
    const roundNum = currentRound + 1;
    const portfolioBeforeReturns = JSON.parse(JSON.stringify(portfolio));
    const portfolioAfterReturns = applyMarketReturns(roundNum);
    
    // Generate optimal portfolio with new structure
    const optimalResult = generateOptimalPortfolio(roundNum, portfolioBeforeReturns);

    setRoundHistory([...roundHistory, {
      round: roundNum,
      portfolioBefore: portfolioBeforeReturns,
      portfolioAfter: portfolioAfterReturns,
      optimal: optimalResult.portfolio,
      switchDetails: optimalResult.switchDetails,
      userSPSelections: optimalResult.userSPSelections,
      incorrectSPs: optimalResult.incorrectSPs,
      optimalMessage: optimalResult.message,
      scenario: MARKET_SCENARIOS[`round${roundNum}`]
    }]);

    setPortfolio(portfolioAfterReturns);
    setCurrentRound(roundNum);
    setGameState(`results${roundNum}`);
    setShowSubmitConfirmation(false);
  };

  const proceedToNextRound = () => {
    if (currentRound < 3) {
      // Only reset portfolio when going from Round 1 to Round 2
      // Round 2 to Round 3 should continue with Round 2's ending portfolio value
      if (currentRound === 1) {
        // Reset portfolio to initial $1M allocation for Round 2
        const initialStocks = JSON.parse(JSON.stringify(INITIAL_STOCKS));
        const initialBonds = JSON.parse(JSON.stringify(INITIAL_BONDS));
        const initialSP = JSON.parse(JSON.stringify(STRUCTURED_PRODUCTS));
        
        // Calculate total allocations
        let totalStocks = 0;
        Object.values(initialStocks).forEach(category => {
          category.forEach(stock => totalStocks += stock.allocation);
        });
        
        let totalBonds = 0;
        Object.values(initialBonds).forEach(category => {
          category.forEach(bond => totalBonds += bond.allocation);
        });
        
        let totalSP = 0;
        Object.values(initialSP).forEach(category => {
          category.forEach(sp => totalSP += sp.allocation);
        });
        
        // Cash is whatever remains to make portfolio sum to 1.0 (100%)
        const calculatedCash = 1 - (totalStocks + totalBonds + totalSP);
        
        setPortfolio({
          stocks: initialStocks,
          bonds: initialBonds,
          structuredProducts: initialSP,
          cash: calculatedCash
        });
      }
      // For Round 2→3, mature structured products with tenor <= 3 months
      else if (currentRound === 2) {
        const currentPortfolio = JSON.parse(JSON.stringify(portfolio));
        let additionalCash = 0;
        
        // Mature all 3-month structured products (tenor <= 3)
        Object.keys(currentPortfolio.structuredProducts).forEach(category => {
          currentPortfolio.structuredProducts[category].forEach(sp => {
            if (sp.tenor <= 3 && sp.allocation > 0) {
              // Add the allocation to cash
              additionalCash += sp.allocation;
              // Zero out the structured product
              sp.allocation = 0;
            }
          });
        });
        
        // Add matured SP values to cash
        currentPortfolio.cash += additionalCash;
        
        setPortfolio(currentPortfolio);
      }
      
      setGameState(`round${currentRound + 1}`);
      setExpandedAssetClasses({});
      setExpandedSectors({});
      setPasswordInput('');
      setPasswordError('');
      setPendingAction(null);
      setHasPortfolioChanged(false);
    }
  };

  const handlePasswordSubmit = () => {
    let requiredPassword = '';
    
    if (pendingAction === 'restart') {
      requiredPassword = ROUND_PASSWORDS.restart;
    } else if (pendingAction === 'round2') {
      requiredPassword = ROUND_PASSWORDS.round2;
    } else if (pendingAction === 'round3') {
      requiredPassword = ROUND_PASSWORDS.round3;
    }
    
    if (passwordInput === requiredPassword) {
      setPasswordError('');
      if (pendingAction === 'restart') {
        setGameState('setup');
        setCurrentRound(0);
        setPortfolio({
          stocks: JSON.parse(JSON.stringify(INITIAL_STOCKS)),
          bonds: JSON.parse(JSON.stringify(INITIAL_BONDS)),
          structuredProducts: JSON.parse(JSON.stringify(STRUCTURED_PRODUCTS)),
          cash: getInitialCash()
        });
        setRoundHistory([]);
        setExpandedAssetClasses({});
        setExpandedSectors({});
        setPasswordInput('');
        setPendingAction(null);
        setHasPortfolioChanged(false);
      } else {
        proceedToNextRound();
      }
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPasswordInput('');
    }
  };

  const requestPasswordFor = (action) => {
    setPendingAction(action);
    setPasswordInput('');
    setPasswordError('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const toggleAssetClass = (key) => {
    setExpandedAssetClasses(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSector = (key) => {
    setExpandedSectors(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSectorLabel = (sector) => {
    const labels = {
      technology: 'Technology',
      healthcare: 'Healthcare',
      consumer: 'Consumer',
      industrials: 'Industrials',
      metalsAndMining: 'Metals & Mining',
      financials: 'Financial',
      utilities: 'Utilities',
      treasuries: 'US Treasuries',
      corporate: 'Corporate Bonds',
      capitalPreservation: 'Capital Preservation',
      income: 'Income',
      growth: 'Growth',
      hedging: 'Hedging'
    };
    return labels[sector] || sector;
  };

  const getRoundCommentary = (roundNum, history) => {
    if (roundNum === 1) {
      return {
        title: "Income Structures in Weak Markets",
        content: [
          "Income Structured Products demonstrated their value by providing steady coupon payments despite negative equity returns",
          "Products like Autocallable Notes deliver predetermined income streams that are less dependent on market direction",
          "These structures offer stability when traditional assets struggle"
        ]
      };
    } else if (roundNum === 2) {
      return {
        title: "Hedging & Capital Preservation in Volatility",
        content: [
          "Hedging protected your portfolio from market stress during Round 2's correction",
          "Minimum Redemption Notes (MRN) limited downside exposure effectively",
          "These instruments act as portfolio insurance during turbulent periods",
          "Minimizing drawdowns is crucial, as losses can be difficult to recover from"
        ]
      };
    } else {
      // Round 3 - Enhanced commentary based on performance
      let additionalCommentary = [];
      
      // Check if they recovered from Round 2 losses
      if (roundHistory.length >= 2) {
        const round1End = roundHistory[0].portfolioAfter;
        const round2End = roundHistory[1].portfolioAfter;
        
        // Calculate Round 1 and Round 2 ending values
        const round1EndValue = calculatePortfolioValue(round1End).total;
        const round2EndValue = calculatePortfolioValue(round2End).total;
        const round3EndValue = calculatePortfolioValue(history.portfolioAfter).total;
        
        // Calculate round 2 loss percentage (declare outside if block for wider scope)
        const round2Loss = ((round2EndValue - round1EndValue) / round1EndValue) * 100;
        
        // Check if they had losses in Round 2
        if (round2EndValue < round1EndValue) {
          if (round3EndValue >= round1EndValue) {
            additionalCommentary.push(`✓ You successfully recovered from Round 2's ${Math.abs(round2Loss).toFixed(1)}% loss and are now back to profitability.`);
          } else {
            additionalCommentary.push(`⚠️ You have not fully recovered from Round 2's ${Math.abs(round2Loss).toFixed(1)}% loss. Recovering from significant losses is challenging - a 50% drop requires a 100% gain just to break even. ${round2EndValue < TOTAL_VALUE * 0.8 ? 'Had you used Hedging or Capital Preservation structures in Round 2, your losses would have been less severe and easier to recover from.' : ''}`);
          }
        } else {
          additionalCommentary.push(`✓ Your portfolio benefited from the V-shaped recovery across all three rounds.`);
        }
        
        // Check if they used growth structures in Round 3
        const usedGrowthSPs = Object.values(history.portfolioBefore.structuredProducts.growth).some(sp => sp.allocation > 0);
        if (!usedGrowthSPs) {
          additionalCommentary.push(`💡 Consider Growth Structures: In recovery markets like Round 3, leveraged participation structures can amplify gains significantly. You missed an opportunity to capture 2x the upside.`);
        }
        
        // Check if they had hedging/capital preservation in Round 2
        if (roundHistory[1]) {
          const hadHedging = Object.values(roundHistory[1].portfolioBefore.structuredProducts.hedging).some(sp => sp.allocation > 0);
          const hadCapPres = Object.values(roundHistory[1].portfolioBefore.structuredProducts.capitalPreservation).some(sp => sp.allocation > 0);
          
          if (!hadHedging && !hadCapPres && round2Loss < -10) {
            additionalCommentary.push(`📊 Important Lesson: Without Hedging or Capital Preservation in Round 2, recovering from losses is more difficult. For example, a 50% drop requires a 100% gain just to break even. Protection in down markets makes subsequent recoveries more achievable.`);
          }
        }
      }
      
      return {
        title: "Growth Structures Capture Recovery",
        content: [
          "Growth Structured Products with leveraged participation amplified gains during the strong recovery",
          "These structures delivered outsized returns compared to direct equity exposure",
          "Lookback features on certain products locked in favorable entry points from Round 2's lows",
          "This maximized upside capture during the subsequent rebound",
          ...additionalCommentary
        ]
      };
    }
  };

  const values = calculatePortfolioValue();

  if (gameState === 'setup' || gameState.startsWith('round')) {
    const isSetup = gameState === 'setup';
    const roundLabel = isSetup ? 'Round 1' : `Round ${currentRound + 1}`;

    return (
      <>
        <style>{`
          @media (max-width: 640px) {
            input[type="number"] {
              font-size: 16px !important;
            }
          }
        `}</style>
      <div className="min-h-screen bg-white p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8 border-b-2 border-slate-800 pb-4 md:pb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-2 text-slate-900" style={{ fontFamily: 'Georgia, serif' }}>
              Investment Simulation Game
            </h1>
            <p className="text-slate-700 text-sm sm:text-base md:text-lg font-medium">{roundLabel}</p>
          </div>

          <div className="mb-6 md:mb-8 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-lg p-6 md:p-8 shadow-lg">
            <div className="text-sm md:text-base text-slate-300 mb-2">Total Portfolio Value</div>
            <div className="text-3xl sm:text-4xl md:text-5xl font-bold">{formatCurrency(values.total)}</div>
          </div>

          <div className="mb-4 sm:mb-6 bg-amber-50 border-2 border-amber-300 rounded-lg p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Minimum Investment Requirement:</span> Structured Products (except Hedging products) require at least $50,000. Stocks and bonds have no minimum.
            </div>
          </div>

          <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6`}>
            {/* Equities */}
            <div className="bg-white border border-slate-300 rounded-lg sm:rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all">
              <div className="bg-slate-800 border-b border-slate-700 p-5">
                <div className="flex items-center justify-between">
                  <button onClick={() => toggleAssetClass('equity')} className="flex items-center gap-2">
                    {expandedAssetClasses['equity'] ? 
                      <ChevronUp className="w-7 h-7 text-emerald-400 hover:text-emerald-300 transition-colors flex-shrink-0" /> : 
                      <ChevronDown className="w-7 h-7 text-emerald-400 hover:text-emerald-300 transition-colors flex-shrink-0" />
                    }
                    <span className="text-xl font-semibold text-white">Equities</span>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-base font-bold text-emerald-400">{formatCurrency(values.equity)}</div>
                      <div className="text-sm text-slate-300">{formatPercent(values.equity / TOTAL_VALUE)}</div>
                    </div>
                    <button
                      onClick={() => setEditingAssetClass(editingAssetClass === 'equity' ? null : 'equity')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                    >
                      {editingAssetClass === 'equity' ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                </div>
                
                {editingAssetClass === 'equity' && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Dollar Amount</label>
                        <input
                          type="number"
                          key={`equity-dollar-${values.equity}`}
                          defaultValue={Math.round(values.equity)}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                          id="equity-dollar-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const dollarAmount = parseFloat(e.target.value);
                              if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                const currentPercent = (values.equity / TOTAL_VALUE) * 100;
                                handleAssetClassAdjust('equity', newPercent - currentPercent);
                                setEditingAssetClass(null);
                              }
                            }
                          }}
                          onChange={(e) => {
                            // Clear percentage input when dollar changes
                            const pctInput = document.getElementById('equity-percent-input');
                            if (pctInput && pctInput !== document.activeElement) {
                              const dollarAmount = parseFloat(e.target.value);
                              if (!isNaN(dollarAmount)) {
                                pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                              }
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Percentage</label>
                        <input
                          type="number"
                          key={`equity-percent-${values.equity}`}
                          defaultValue={((values.equity / TOTAL_VALUE) * 100).toFixed(1)}
                          step="0.1"
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                          id="equity-percent-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const targetPercent = parseFloat(e.target.value);
                              if (!isNaN(targetPercent) && targetPercent >= 0) {
                                const currentPercent = (values.equity / TOTAL_VALUE) * 100;
                                handleAssetClassAdjust('equity', targetPercent - currentPercent);
                                setEditingAssetClass(null);
                              }
                            }
                          }}
                          onChange={(e) => {
                            // Clear dollar input when percentage changes
                            const dollarInput = document.getElementById('equity-dollar-input');
                            if (dollarInput && dollarInput !== document.activeElement) {
                              const targetPercent = parseFloat(e.target.value);
                              if (!isNaN(targetPercent)) {
                                dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-slate-400">Press Enter or click Done to apply</span>
                      <button
                        onClick={() => {
                          const dollarInput = document.getElementById('equity-dollar-input');
                          const pctInput = document.getElementById('equity-percent-input');
                          
                          // Try dollar input first
                          if (dollarInput && dollarInput.value) {
                            const dollarAmount = parseFloat(dollarInput.value);
                            if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                              const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                              const currentPercent = (values.equity / TOTAL_VALUE) * 100;
                              if (Math.abs(newPercent - currentPercent) > 0.01) {
                                handleAssetClassAdjust('equity', newPercent - currentPercent);
                              }
                            }
                          }
                          // Or try percentage input
                          else if (pctInput && pctInput.value) {
                            const targetPercent = parseFloat(pctInput.value);
                            if (!isNaN(targetPercent) && targetPercent >= 0) {
                              const currentPercent = (values.equity / TOTAL_VALUE) * 100;
                              if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                handleAssetClassAdjust('equity', targetPercent - currentPercent);
                              }
                            }
                          }
                          setEditingAssetClass(null);
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {expandedAssetClasses['equity'] && (
                <div className="p-5 bg-white space-y-2 sm:space-y-3">
                  {Object.keys(portfolio.stocks).map(sector => {
                    const sectorTotal = portfolio.stocks[sector].reduce((sum, stock) => sum + stock.allocation, 0);
                    const isSectorExpanded = expandedSectors[`equity-${sector}`];
                    
                    return (
                      <div key={sector} className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors">
                        <div className="bg-slate-100 p-3 sm:p-4">
                          <div className="flex items-center justify-between">
                            <button onClick={() => toggleSector(`equity-${sector}`)} className="flex items-center gap-2">
                              {isSectorExpanded ? 
                                <ChevronUp className="w-6 h-6 text-slate-600 hover:text-slate-800 transition-colors flex-shrink-0" /> : 
                                <ChevronDown className="w-6 h-6 text-slate-600 hover:text-slate-800 transition-colors flex-shrink-0" />
                              }
                              <span className="font-semibold text-slate-900 text-base">{getSectorLabel(sector)}</span>
                            </button>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-base font-bold text-emerald-600">{formatCurrency(sectorTotal * TOTAL_VALUE)}</div>
                                <div className="text-sm text-slate-600">{formatPercent(sectorTotal)}</div>
                              </div>
                              <button
                                onClick={() => setEditingSector(editingSector === `equity-${sector}` ? null : `equity-${sector}`)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors"
                              >
                                {editingSector === `equity-${sector}` ? 'Cancel' : 'Edit'}
                              </button>
                            </div>
                          </div>
                          
                          {editingSector === `equity-${sector}` && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-slate-600 mb-1">Dollar Amount</label>
                                  <input
                                    type="number"
                                    key={`equity-${sector}-dollar-${sectorTotal}`}
                                    defaultValue={Math.round(sectorTotal * TOTAL_VALUE)}
                                    id={`equity-${sector}-dollar-input`}
                                    className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const dollarAmount = parseFloat(e.target.value);
                                        if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                          const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                          handleSectorAdjust('stocks', sector, newPercent - (sectorTotal * 100));
                                          setEditingSector(null);
                                        }
                                      }
                                    }}
                                    onChange={(e) => {
                                      const pctInput = document.getElementById(`equity-${sector}-percent-input`);
                                      if (pctInput && pctInput !== document.activeElement) {
                                        const dollarAmount = parseFloat(e.target.value);
                                        if (!isNaN(dollarAmount)) {
                                          pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                                        }
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-600 mb-1">Percentage</label>
                                  <input
                                    type="number"
                                    key={`equity-${sector}-percent-${sectorTotal}`}
                                    defaultValue={(sectorTotal * 100).toFixed(1)}
                                    step="0.1"
                                    id={`equity-${sector}-percent-input`}
                                    className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const targetPercent = parseFloat(e.target.value);
                                        if (!isNaN(targetPercent) && targetPercent >= 0) {
                                          handleSectorAdjust('stocks', sector, targetPercent - (sectorTotal * 100));
                                          setEditingSector(null);
                                        }
                                      }
                                    }}
                                    onChange={(e) => {
                                      const dollarInput = document.getElementById(`equity-${sector}-dollar-input`);
                                      if (dollarInput && dollarInput !== document.activeElement) {
                                        const targetPercent = parseFloat(e.target.value);
                                        if (!isNaN(targetPercent)) {
                                          dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <span className="text-xs text-slate-500">Press Enter or click Apply</span>
                                <button
                                  onClick={() => {
                                    const dollarInput = document.getElementById(`equity-${sector}-dollar-input`);
                                    const pctInput = document.getElementById(`equity-${sector}-percent-input`);
                                    
                                    if (dollarInput && dollarInput.value) {
                                      const dollarAmount = parseFloat(dollarInput.value);
                                      if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                        const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                        const currentPercent = sectorTotal * 100;
                                        if (Math.abs(newPercent - currentPercent) > 0.01) {
                                          handleSectorAdjust('stocks', sector, newPercent - currentPercent);
                                        }
                                      }
                                    }
                                    else if (pctInput && pctInput.value) {
                                      const targetPercent = parseFloat(pctInput.value);
                                      if (!isNaN(targetPercent) && targetPercent >= 0) {
                                        const currentPercent = sectorTotal * 100;
                                        if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                          handleSectorAdjust('stocks', sector, targetPercent - currentPercent);
                                        }
                                      }
                                    }
                                    setEditingSector(null);
                                  }}
                                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                                >
                                  Apply
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {isSectorExpanded && (
                          <div className="bg-white p-4 space-y-2 border-t border-slate-200">
                            {portfolio.stocks[sector].map((stock, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm border-b border-slate-100 pb-3 last:border-0">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0 mr-4">
                                      <div className="font-semibold text-slate-900">{stock.name}</div>
                                      <div className="text-slate-500 text-xs mt-0.5">{stock.ticker}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-right">
                                        <div className="font-semibold text-emerald-600 text-sm">{formatCurrency(stock.allocation * TOTAL_VALUE)}</div>
                                        <div className="text-slate-600 text-xs">{formatPercent(stock.allocation)}</div>
                                      </div>
                                      <button
                                        onClick={() => setEditingInstrument(editingInstrument === `stock-${sector}-${idx}` ? null : `stock-${sector}-${idx}`)}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                                      >
                                        {editingInstrument === `stock-${sector}-${idx}` ? 'Cancel' : 'Edit'}
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {editingInstrument === `stock-${sector}-${idx}` && (
                                    <div className="mt-2 pt-2 border-t border-slate-100">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs text-slate-500 mb-1">Dollar Amount</label>
                                          <input
                                            type="number"
                                            key={`stock-${sector}-${idx}-dollar-${stock.allocation}`}
                                            defaultValue={Math.round(stock.allocation * TOTAL_VALUE)}
                                            id={`stock-${sector}-${idx}-dollar-input`}
                                            className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-emerald-500"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                const dollarAmount = parseFloat(e.target.value);
                                                if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                                  const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                                  handleInstrumentAdjust('stocks', sector, idx, newPercent - (stock.allocation * 100));
                                                  setEditingInstrument(null);
                                                }
                                              }
                                            }}
                                            onChange={(e) => {
                                              const pctInput = document.getElementById(`stock-${sector}-${idx}-percent-input`);
                                              if (pctInput && pctInput !== document.activeElement) {
                                                const dollarAmount = parseFloat(e.target.value);
                                                if (!isNaN(dollarAmount)) {
                                                  pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-slate-500 mb-1">Percentage</label>
                                          <input
                                            type="number"
                                            key={`stock-${sector}-${idx}-percent-${stock.allocation}`}
                                            defaultValue={(stock.allocation * 100).toFixed(1)}
                                            step="0.1"
                                            id={`stock-${sector}-${idx}-percent-input`}
                                            className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-emerald-500"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                const targetPercent = parseFloat(e.target.value);
                                                if (!isNaN(targetPercent) && targetPercent >= 0) {
                                                  handleInstrumentAdjust('stocks', sector, idx, targetPercent - (stock.allocation * 100));
                                                  setEditingInstrument(null);
                                                }
                                              }
                                            }}
                                            onChange={(e) => {
                                              const dollarInput = document.getElementById(`stock-${sector}-${idx}-dollar-input`);
                                              if (dollarInput && dollarInput !== document.activeElement) {
                                                const targetPercent = parseFloat(e.target.value);
                                                if (!isNaN(targetPercent)) {
                                                  dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="mt-1 flex items-center justify-between">
                                        <span className="text-xs text-slate-400">Enter or Apply</span>
                                        <button
                                          onClick={() => {
                                            const dollarInput = document.getElementById(`stock-${sector}-${idx}-dollar-input`);
                                            const pctInput = document.getElementById(`stock-${sector}-${idx}-percent-input`);
                                            
                                            if (dollarInput && dollarInput.value) {
                                              const dollarAmount = parseFloat(dollarInput.value);
                                              if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                                const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                                const currentPercent = stock.allocation * 100;
                                                if (Math.abs(newPercent - currentPercent) > 0.01) {
                                                  handleInstrumentAdjust('stocks', sector, idx, newPercent - currentPercent);
                                                }
                                              }
                                            }
                                            else if (pctInput && pctInput.value) {
                                              const targetPercent = parseFloat(pctInput.value);
                                              if (!isNaN(targetPercent) && targetPercent >= 0) {
                                                const currentPercent = stock.allocation * 100;
                                                if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                                  handleInstrumentAdjust('stocks', sector, idx, targetPercent - currentPercent);
                                                }
                                              }
                                            }
                                            setEditingInstrument(null);
                                          }}
                                          className="bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-0.5 rounded text-xs font-medium transition-colors"
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bonds */}
            <div className="bg-white border border-slate-300 rounded-lg sm:rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all">
              <div className="bg-slate-800 border-b border-slate-700 p-5">
                <div className="flex items-center justify-between">
                  <button onClick={() => toggleAssetClass('bonds')} className="flex items-center gap-2">
                    {expandedAssetClasses['bonds'] ? 
                      <ChevronUp className="w-7 h-7 text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0" /> : 
                      <ChevronDown className="w-7 h-7 text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0" />
                    }
                    <span className="text-xl font-semibold text-white">Bonds</span>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-base font-bold text-blue-400">{formatCurrency(values.bonds)}</div>
                      <div className="text-sm text-slate-300">{formatPercent(values.bonds / TOTAL_VALUE)}</div>
                    </div>
                    <button
                      onClick={() => setEditingAssetClass(editingAssetClass === 'bonds' ? null : 'bonds')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                    >
                      {editingAssetClass === 'bonds' ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                </div>
                
                {editingAssetClass === 'bonds' && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Dollar Amount</label>
                        <input
                          type="number"
                          key={`bonds-dollar-${values.bonds}`}
                          defaultValue={Math.round(values.bonds)}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                          id="bonds-dollar-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const dollarAmount = parseFloat(e.target.value);
                              if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                const currentPercent = (values.bonds / TOTAL_VALUE) * 100;
                                handleAssetClassAdjust('bonds', newPercent - currentPercent);
                                setEditingAssetClass(null);
                              }
                            }
                          }}
                          onChange={(e) => {
                            const pctInput = document.getElementById('bonds-percent-input');
                            if (pctInput && pctInput !== document.activeElement) {
                              const dollarAmount = parseFloat(e.target.value);
                              if (!isNaN(dollarAmount)) {
                                pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                              }
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Percentage</label>
                        <input
                          type="number"
                          key={`bonds-percent-${values.bonds}`}
                          defaultValue={((values.bonds / TOTAL_VALUE) * 100).toFixed(1)}
                          step="0.1"
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                          id="bonds-percent-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const targetPercent = parseFloat(e.target.value);
                              if (!isNaN(targetPercent) && targetPercent >= 0) {
                                const currentPercent = (values.bonds / TOTAL_VALUE) * 100;
                                handleAssetClassAdjust('bonds', targetPercent - currentPercent);
                                setEditingAssetClass(null);
                              }
                            }
                          }}
                          onChange={(e) => {
                            const dollarInput = document.getElementById('bonds-dollar-input');
                            if (dollarInput && dollarInput !== document.activeElement) {
                              const targetPercent = parseFloat(e.target.value);
                              if (!isNaN(targetPercent)) {
                                dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-slate-400">Press Enter or click Done to apply</span>
                      <button
                        onClick={() => {
                          const dollarInput = document.getElementById('bonds-dollar-input');
                          const pctInput = document.getElementById('bonds-percent-input');
                          
                          if (dollarInput && dollarInput.value) {
                            const dollarAmount = parseFloat(dollarInput.value);
                            if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                              const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                              const currentPercent = (values.bonds / TOTAL_VALUE) * 100;
                              if (Math.abs(newPercent - currentPercent) > 0.01) {
                                handleAssetClassAdjust('bonds', newPercent - currentPercent);
                              }
                            }
                          }
                          else if (pctInput && pctInput.value) {
                            const targetPercent = parseFloat(pctInput.value);
                            if (!isNaN(targetPercent) && targetPercent >= 0) {
                              const currentPercent = (values.bonds / TOTAL_VALUE) * 100;
                              if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                handleAssetClassAdjust('bonds', targetPercent - currentPercent);
                              }
                            }
                          }
                          setEditingAssetClass(null);
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {expandedAssetClasses['bonds'] && (
                <div className="p-5 bg-white space-y-2 sm:space-y-3">
                  {Object.keys(portfolio.bonds).map(category => {
                    const categoryTotal = portfolio.bonds[category].reduce((sum, bond) => sum + bond.allocation, 0);
                    const isCategoryExpanded = expandedSectors[`bond-${category}`];
                    
                    return (
                      <div key={category} className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors">
                        <div className="bg-slate-100 p-3 sm:p-4">
                          <div className="flex items-center justify-between">
                            <button onClick={() => toggleSector(`bond-${category}`)} className="flex items-center gap-2">
                              {isCategoryExpanded ? 
                                <ChevronUp className="w-6 h-6 text-slate-600 hover:text-slate-800 transition-colors flex-shrink-0" /> : 
                                <ChevronDown className="w-6 h-6 text-slate-600 hover:text-slate-800 transition-colors flex-shrink-0" />
                              }
                              <span className="font-semibold text-slate-900 text-base">{getSectorLabel(category)}</span>
                            </button>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-base font-bold text-blue-600">{formatCurrency(categoryTotal * TOTAL_VALUE)}</div>
                                <div className="text-sm text-slate-600">{formatPercent(categoryTotal)}</div>
                              </div>
                              <button
                                onClick={() => setEditingSector(editingSector === `bond-${category}` ? null : `bond-${category}`)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors"
                              >
                                {editingSector === `bond-${category}` ? 'Cancel' : 'Edit'}
                              </button>
                            </div>
                          </div>
                          
                          {editingSector === `bond-${category}` && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-slate-600 mb-1">Dollar Amount</label>
                                  <input
                                    type="number"
                                    key={`bond-${category}-dollar-${categoryTotal}`}
                                    defaultValue={Math.round(categoryTotal * TOTAL_VALUE)}
                                    id={`bond-${category}-dollar-input`}
                                    className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const dollarAmount = parseFloat(e.target.value);
                                        if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                          const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                          handleSectorAdjust('bonds', category, newPercent - (categoryTotal * 100));
                                          setEditingSector(null);
                                        }
                                      }
                                    }}
                                    onChange={(e) => {
                                      const pctInput = document.getElementById(`bond-${category}-percent-input`);
                                      if (pctInput && pctInput !== document.activeElement) {
                                        const dollarAmount = parseFloat(e.target.value);
                                        if (!isNaN(dollarAmount)) {
                                          pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                                        }
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-600 mb-1">Percentage</label>
                                  <input
                                    type="number"
                                    key={`bond-${category}-percent-${categoryTotal}`}
                                    defaultValue={(categoryTotal * 100).toFixed(1)}
                                    step="0.1"
                                    id={`bond-${category}-percent-input`}
                                    className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const targetPercent = parseFloat(e.target.value);
                                        if (!isNaN(targetPercent) && targetPercent >= 0) {
                                          handleSectorAdjust('bonds', category, targetPercent - (categoryTotal * 100));
                                          setEditingSector(null);
                                        }
                                      }
                                    }}
                                    onChange={(e) => {
                                      const dollarInput = document.getElementById(`bond-${category}-dollar-input`);
                                      if (dollarInput && dollarInput !== document.activeElement) {
                                        const targetPercent = parseFloat(e.target.value);
                                        if (!isNaN(targetPercent)) {
                                          dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <span className="text-xs text-slate-500">Press Enter or click Apply</span>
                                <button
                                  onClick={() => {
                                    const dollarInput = document.getElementById(`bond-${category}-dollar-input`);
                                    const pctInput = document.getElementById(`bond-${category}-percent-input`);
                                    
                                    if (dollarInput && dollarInput.value) {
                                      const dollarAmount = parseFloat(dollarInput.value);
                                      if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                        const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                        const currentPercent = categoryTotal * 100;
                                        if (Math.abs(newPercent - currentPercent) > 0.01) {
                                          handleSectorAdjust('bonds', category, newPercent - currentPercent);
                                        }
                                      }
                                    }
                                    else if (pctInput && pctInput.value) {
                                      const targetPercent = parseFloat(pctInput.value);
                                      if (!isNaN(targetPercent) && targetPercent >= 0) {
                                        const currentPercent = categoryTotal * 100;
                                        if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                          handleSectorAdjust('bonds', category, targetPercent - currentPercent);
                                        }
                                      }
                                    }
                                    setEditingSector(null);
                                  }}
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                                >
                                  Apply
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        
                        {isCategoryExpanded && (
                          <div className="bg-white p-4 space-y-2 border-t border-slate-200">
                            {portfolio.bonds[category].map((bond, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm border-b border-slate-100 pb-3 last:border-0">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0 mr-4">
                                      <div className="font-semibold text-slate-900">{bond.name}</div>
                                      <div className="text-slate-500 text-xs mt-0.5">{bond.ticker}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-right">
                                        <div className="font-semibold text-blue-600 text-sm">{formatCurrency(bond.allocation * TOTAL_VALUE)}</div>
                                        <div className="text-slate-600 text-xs">{formatPercent(bond.allocation)}</div>
                                      </div>
                                      <button
                                        onClick={() => setEditingInstrument(editingInstrument === `bond-${category}-${idx}` ? null : `bond-${category}-${idx}`)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                                      >
                                        {editingInstrument === `bond-${category}-${idx}` ? 'Cancel' : 'Edit'}
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {editingInstrument === `bond-${category}-${idx}` && (
                                    <div className="mt-2 pt-2 border-t border-slate-100">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs text-slate-500 mb-1">Dollar Amount</label>
                                          <input
                                            type="number"
                                            key={`bond-${category}-${idx}-dollar-${bond.allocation}`}
                                            defaultValue={Math.round(bond.allocation * TOTAL_VALUE)}
                                            id={`bond-${category}-${idx}-dollar-input`}
                                            className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-500"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                const dollarAmount = parseFloat(e.target.value);
                                                if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                                  const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                                  handleInstrumentAdjust('bonds', category, idx, newPercent - (bond.allocation * 100));
                                                  setEditingInstrument(null);
                                                }
                                              }
                                            }}
                                            onChange={(e) => {
                                              const pctInput = document.getElementById(`bond-${category}-${idx}-percent-input`);
                                              if (pctInput && pctInput !== document.activeElement) {
                                                const dollarAmount = parseFloat(e.target.value);
                                                if (!isNaN(dollarAmount)) {
                                                  pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-slate-500 mb-1">Percentage</label>
                                          <input
                                            type="number"
                                            key={`bond-${category}-${idx}-percent-${bond.allocation}`}
                                            defaultValue={(bond.allocation * 100).toFixed(1)}
                                            step="0.1"
                                            id={`bond-${category}-${idx}-percent-input`}
                                            className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-500"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                const targetPercent = parseFloat(e.target.value);
                                                if (!isNaN(targetPercent) && targetPercent >= 0) {
                                                  handleInstrumentAdjust('bonds', category, idx, targetPercent - (bond.allocation * 100));
                                                  setEditingInstrument(null);
                                                }
                                              }
                                            }}
                                            onChange={(e) => {
                                              const dollarInput = document.getElementById(`bond-${category}-${idx}-dollar-input`);
                                              if (dollarInput && dollarInput !== document.activeElement) {
                                                const targetPercent = parseFloat(e.target.value);
                                                if (!isNaN(targetPercent)) {
                                                  dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="mt-1 flex items-center justify-between">
                                        <span className="text-xs text-slate-400">Enter or Apply</span>
                                        <button
                                          onClick={() => {
                                            const dollarInput = document.getElementById(`bond-${category}-${idx}-dollar-input`);
                                            const pctInput = document.getElementById(`bond-${category}-${idx}-percent-input`);
                                            
                                            if (dollarInput && dollarInput.value) {
                                              const dollarAmount = parseFloat(dollarInput.value);
                                              if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                                const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                                const currentPercent = bond.allocation * 100;
                                                if (Math.abs(newPercent - currentPercent) > 0.01) {
                                                  handleInstrumentAdjust('bonds', category, idx, newPercent - currentPercent);
                                                }
                                              }
                                            }
                                            else if (pctInput && pctInput.value) {
                                              const targetPercent = parseFloat(pctInput.value);
                                              if (!isNaN(targetPercent) && targetPercent >= 0) {
                                                const currentPercent = bond.allocation * 100;
                                                if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                                  handleInstrumentAdjust('bonds', category, idx, targetPercent - currentPercent);
                                                }
                                              }
                                            }
                                            setEditingInstrument(null);
                                          }}
                                          className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-medium transition-colors"
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Structured Products — only visible if participant holds SPs (e.g. accepted a switch in a prior round) */}
            {/* Structured Products */}
            <div className="bg-white border border-slate-300 rounded-lg sm:rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all">
              <div className="bg-slate-800 border-b border-slate-700 p-5">
                <div className="flex items-center justify-between">
                  <button onClick={() => toggleAssetClass('sp')} className="flex items-center gap-2">
                    {expandedAssetClasses['sp'] ? 
                      <ChevronUp className="w-7 h-7 text-purple-400 hover:text-purple-300 transition-colors flex-shrink-0" /> : 
                      <ChevronDown className="w-7 h-7 text-purple-400 hover:text-purple-300 transition-colors flex-shrink-0" />
                    }
                    <span className="text-xl font-semibold text-white">Structured Products</span>
                  </button>
                  <div className="text-right">
                    <div className="text-base font-bold text-purple-400">{formatCurrency(values.structuredProducts)}</div>
                    <div className="text-sm text-slate-300">{formatPercent(values.structuredProducts / TOTAL_VALUE)}</div>
                  </div>
                </div>
              </div>

              {expandedAssetClasses['sp'] && (
                <div className="p-5 bg-white space-y-2 sm:space-y-3">
                  {Object.keys(portfolio.structuredProducts).map(category => {
                    const categoryTotal = portfolio.structuredProducts[category].reduce((sum, sp) => sum + sp.allocation, 0);
                    const isCategoryExpanded = expandedSectors[`sp-${category}`];
                    
                    return (
                      <div key={category} className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors">
                        <div className="bg-slate-100 p-3 sm:p-4">
                          <div className="flex items-center justify-between">
                            <button onClick={() => toggleSector(`sp-${category}`)} className="flex items-center gap-2">
                              {isCategoryExpanded ? 
                                <ChevronUp className="w-6 h-6 text-slate-600 hover:text-slate-800 transition-colors flex-shrink-0" /> : 
                                <ChevronDown className="w-6 h-6 text-slate-600 hover:text-slate-800 transition-colors flex-shrink-0" />
                              }
                              <span className="font-semibold text-slate-900 text-base">{getSectorLabel(category)}</span>
                            </button>
                            <div className="text-right">
                              <div className="text-base font-bold text-purple-600">{formatCurrency(categoryTotal * TOTAL_VALUE)}</div>
                              <div className="text-sm text-slate-600">{formatPercent(categoryTotal)}</div>
                            </div>
                          </div>
                        </div>

                        
                        {isCategoryExpanded && (
                          <div className="bg-white p-4 space-y-2 border-t border-slate-200">
                            {portfolio.structuredProducts[category].filter(sp => {
                              // Show SP if current round (1-3) is in its display array. For setup (round 0), use round 1.
                              const displayRound = currentRound + 1;
                              // Show if in display array OR if user has a position (prevents "ghost positions")
                              return (sp.display && sp.display.includes(displayRound)) || sp.allocation > 0;
                            }).map((sp, displayIdx) => {
                              // Get the actual index in the full array (for handleInstrumentAdjust)
                              const idx = portfolio.structuredProducts[category].indexOf(sp);
                              return (
                              <div key={idx} className="flex items-center justify-between text-sm border-b border-slate-100 pb-3 last:border-0">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0 mr-4">
                                      <div className="font-semibold text-slate-900">{sp.name}</div>
                                      <div className="text-slate-500 text-xs mt-0.5">{sp.theme}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-right">
                                        <div className="font-semibold text-purple-600 text-sm">{formatCurrency(sp.allocation * TOTAL_VALUE)}</div>
                                        <div className="text-xs text-slate-600">{formatPercent(sp.allocation)}</div>
                                      </div>
                                      <button
                                        onClick={() => setEditingInstrument(editingInstrument === `sp-${category}-${idx}` ? null : `sp-${category}-${idx}`)}
                                        className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                                      >
                                        {editingInstrument === `sp-${category}-${idx}` ? 'Cancel' : 'Edit'}
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {editingInstrument === `sp-${category}-${idx}` && (
                                    <div className="mt-2 pt-2 border-t border-slate-100">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs text-slate-500 mb-1">Dollar Amount</label>
                                          <input
                                            type="number"
                                            key={`sp-${category}-${idx}-dollar-${sp.allocation}`}
                                            defaultValue={Math.round(sp.allocation * TOTAL_VALUE)}
                                            id={`sp-${category}-${idx}-dollar-input`}
                                            className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-purple-500"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                const dollarAmount = parseFloat(e.target.value);
                                                if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                                  const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                                  handleInstrumentAdjust('sp', category, idx, newPercent - (sp.allocation * 100));
                                                  setEditingInstrument(null);
                                                }
                                              }
                                            }}
                                            onChange={(e) => {
                                              const pctInput = document.getElementById(`sp-${category}-${idx}-percent-input`);
                                              if (pctInput && pctInput !== document.activeElement) {
                                                const dollarAmount = parseFloat(e.target.value);
                                                if (!isNaN(dollarAmount)) {
                                                  pctInput.value = ((dollarAmount / TOTAL_VALUE) * 100).toFixed(1);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-slate-500 mb-1">Percentage</label>
                                          <input
                                            type="number"
                                            key={`sp-${category}-${idx}-percent-${sp.allocation}`}
                                            defaultValue={(sp.allocation * 100).toFixed(1)}
                                            step="0.1"
                                            id={`sp-${category}-${idx}-percent-input`}
                                            className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-purple-500"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                const targetPercent = parseFloat(e.target.value);
                                                if (!isNaN(targetPercent) && targetPercent >= 0) {
                                                  handleInstrumentAdjust('sp', category, idx, targetPercent - (sp.allocation * 100));
                                                  setEditingInstrument(null);
                                                }
                                              }
                                            }}
                                            onChange={(e) => {
                                              const dollarInput = document.getElementById(`sp-${category}-${idx}-dollar-input`);
                                              if (dollarInput && dollarInput !== document.activeElement) {
                                                const targetPercent = parseFloat(e.target.value);
                                                if (!isNaN(targetPercent)) {
                                                  dollarInput.value = Math.round((targetPercent / 100) * TOTAL_VALUE);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div className="mt-1 flex items-center justify-between">
                                        <span className="text-xs text-slate-400">Enter or Apply</span>
                                        <button
                                          onClick={() => {
                                            const dollarInput = document.getElementById(`sp-${category}-${idx}-dollar-input`);
                                            const pctInput = document.getElementById(`sp-${category}-${idx}-percent-input`);
                                            
                                            if (dollarInput && dollarInput.value) {
                                              const dollarAmount = parseFloat(dollarInput.value);
                                              if (!isNaN(dollarAmount) && dollarAmount >= 0) {
                                                const newPercent = (dollarAmount / TOTAL_VALUE) * 100;
                                                const currentPercent = sp.allocation * 100;
                                                if (Math.abs(newPercent - currentPercent) > 0.01) {
                                                  handleInstrumentAdjust('sp', category, idx, newPercent - currentPercent);
                                                }
                                              }
                                            }
                                            else if (pctInput && pctInput.value) {
                                              const targetPercent = parseFloat(pctInput.value);
                                              if (!isNaN(targetPercent) && targetPercent >= 0) {
                                                const currentPercent = sp.allocation * 100;
                                                if (Math.abs(targetPercent - currentPercent) > 0.01) {
                                                  handleInstrumentAdjust('sp', category, idx, targetPercent - currentPercent);
                                                }
                                              }
                                            }
                                            setEditingInstrument(null);
                                          }}
                                          className="bg-purple-500 hover:bg-purple-600 text-white px-2 py-0.5 rounded text-xs font-medium transition-colors"
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );})}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cash */}
            <div className="bg-white border border-slate-300 rounded-lg sm:rounded-xl overflow-hidden shadow-sm">
              <div className="bg-slate-800 p-5">
                <div className="flex items-center gap-2 flex-1 mb-3">
                  <span className="text-xl font-semibold text-white">Cash</span>
                  <div className="flex items-center gap-3 ml-auto">
                    <span className="text-base font-bold text-slate-300">{formatCurrency(values.cash)}</span>
                    <span className="text-base text-slate-400">{formatPercent(values.cash / TOTAL_VALUE)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Confirmation Dialog */}
          {showSubmitConfirmation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-4 sm:p-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  {hasPortfolioChanged ? 'Confirm Submission' : 'No Changes Made'}
                </h3>
                <p className="text-slate-700 mb-6">
                  {hasPortfolioChanged 
                    ? 'Are you sure you want to submit? You cannot return once you submit.'
                    : "You haven't made any changes to your portfolio. Are you sure you want to proceed? You cannot return once you submit."}
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowSubmitConfirmation(false)}
                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-2 rounded-lg font-semibold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitRound}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-4 sm:px-6 py-3 rounded-lg text-sm sm:text-base font-semibold transition-all"
                  >
                    Yes, Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <button
              onClick={handleSubmitClick}
              className="bg-slate-900 hover:bg-slate-800 text-white px-6 sm:px-8 md:px-12 py-3 md:py-4 w-full sm:w-auto rounded-lg text-base md:text-lg font-semibold shadow-lg transition-all hover:shadow-xl hover:scale-105"
            >
              {isSetup ? 'Submit Round 1' : `Submit Round ${currentRound + 1}`}
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  if (gameState.startsWith('results')) {
    const roundNum = parseInt(gameState.replace('results', ''));
    const history = roundHistory[roundNum - 1];
    
    // Safety check - ensure history exists
    if (!history || !history.portfolioBefore || !history.portfolioAfter) {
      return (
        <div className="min-h-screen bg-white p-8 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-slate-700">Loading results...</p>
          </div>
        </div>
      );
    }
    
    // Calculate values BEFORE returns for client portfolio
    let beforeEquity = 0, beforeBonds = 0, beforeSP = 0, beforeCash = 0;
    Object.values(history.portfolioBefore.stocks).forEach(sector => {
      sector.forEach(stock => beforeEquity += stock.allocation * TOTAL_VALUE);
    });
    Object.values(history.portfolioBefore.bonds).forEach(category => {
      category.forEach(bond => beforeBonds += bond.allocation * TOTAL_VALUE);
    });
    Object.values(history.portfolioBefore.structuredProducts).forEach(category => {
      category.forEach(sp => beforeSP += sp.allocation * TOTAL_VALUE);
    });
    beforeCash = history.portfolioBefore.cash * TOTAL_VALUE;
    const beforeTotal = beforeEquity + beforeBonds + beforeSP + beforeCash;
    
    // Calculate values AFTER returns for client portfolio
    let afterEquity = 0, afterBonds = 0, afterSP = 0, afterCash = 0;
    Object.values(history.portfolioAfter.stocks).forEach(sector => {
      sector.forEach(stock => afterEquity += stock.allocation * TOTAL_VALUE);
    });
    Object.values(history.portfolioAfter.bonds).forEach(category => {
      category.forEach(bond => afterBonds += bond.allocation * TOTAL_VALUE);
    });
    Object.values(history.portfolioAfter.structuredProducts).forEach(category => {
      category.forEach(sp => afterSP += sp.allocation * TOTAL_VALUE);
    });
    afterCash = history.portfolioAfter.cash * TOTAL_VALUE;
    const afterTotal = afterEquity + afterBonds + afterSP + afterCash;
    
    const clientReturn = ((afterTotal - beforeTotal) / beforeTotal);
    const clientEquityReturn = (afterEquity - beforeEquity) / beforeTotal;
    const clientBondsReturn = (afterBonds - beforeBonds) / beforeTotal;
    const clientSPReturn = (afterSP - beforeSP) / beforeTotal;
    
    // Calculate OPTIMAL portfolio values BEFORE and AFTER returns
    const scenario = MARKET_SCENARIOS[`round${roundNum}`];
    
    // Optimal BEFORE returns
    let optimalBeforeEquity = 0, optimalBeforeBonds = 0, optimalBeforeSP = 0, optimalBeforeCash = 0;
    Object.values(history.optimal.stocks).forEach(sector => {
      sector.forEach(stock => optimalBeforeEquity += stock.allocation * TOTAL_VALUE);
    });
    Object.values(history.optimal.bonds).forEach(category => {
      category.forEach(bond => optimalBeforeBonds += bond.allocation * TOTAL_VALUE);
    });
    Object.values(history.optimal.structuredProducts).forEach(category => {
      category.forEach(sp => optimalBeforeSP += sp.allocation * TOTAL_VALUE);
    });
    optimalBeforeCash = history.optimal.cash * TOTAL_VALUE;
    const optimalBeforeTotal = optimalBeforeEquity + optimalBeforeBonds + optimalBeforeSP + optimalBeforeCash;
    
    // Apply returns to optimal portfolio
    let optimalAfter = JSON.parse(JSON.stringify(history.optimal));
    
    const roundKey = `round${roundNum}`;
    Object.keys(optimalAfter.stocks).forEach(sector => {
      optimalAfter.stocks[sector].forEach(stock => {
        stock.allocation *= (1 + (STOCK_RETURNS[sector]?.[roundKey]?.[stock.ticker] ?? 0));
      });
    });

    Object.keys(optimalAfter.bonds).forEach(category => {
      optimalAfter.bonds[category].forEach(bond => {
        bond.allocation *= (1 + (BOND_RETURNS[category]?.[roundKey]?.[bond.ticker] ?? 0));
      });
    });

    Object.keys(optimalAfter.structuredProducts).forEach(category => {
      optimalAfter.structuredProducts[category].forEach(sp => {
        sp.allocation *= (1 + (SP_RETURNS[roundKey]?.[sp.ticker] ?? 0));
      });
    });

    optimalAfter.cash *= (1 + scenario.cash);
    
    // Optimal AFTER returns
    let optimalAfterEquity = 0, optimalAfterBonds = 0, optimalAfterSP = 0, optimalAfterCash = 0;
    Object.values(optimalAfter.stocks).forEach(sector => {
      sector.forEach(stock => optimalAfterEquity += stock.allocation * TOTAL_VALUE);
    });
    Object.values(optimalAfter.bonds).forEach(category => {
      category.forEach(bond => optimalAfterBonds += bond.allocation * TOTAL_VALUE);
    });
    Object.values(optimalAfter.structuredProducts).forEach(category => {
      category.forEach(sp => optimalAfterSP += sp.allocation * TOTAL_VALUE);
    });
    optimalAfterCash = optimalAfter.cash * TOTAL_VALUE;
    const optimalAfterTotal = optimalAfterEquity + optimalAfterBonds + optimalAfterSP + optimalAfterCash;
    
    const optimalReturn = ((optimalAfterTotal - optimalBeforeTotal) / optimalBeforeTotal);
    const optimalEquityReturn = (optimalAfterEquity - optimalBeforeEquity) / optimalBeforeTotal;
    const optimalBondsReturn = (optimalAfterBonds - optimalBeforeBonds) / optimalBeforeTotal;
    const optimalSPReturn = (optimalAfterSP - optimalBeforeSP) / optimalBeforeTotal;

    const commentary = getRoundCommentary(roundNum, history);

    return (
      <>
        <style>{`
          @media (max-width: 640px) {
            input[type="number"] {
              font-size: 16px !important;
            }
          }
        `}</style>
      <div className="min-h-screen bg-white p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8 border-b-2 border-slate-800 pb-4 md:pb-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-2 md:mb-3 text-slate-900" style={{ fontFamily: 'Georgia, serif' }}>
              Round {roundNum} Results
            </h1>
            <p className="text-slate-700 text-base sm:text-lg md:text-xl font-medium">{history.scenario.name}</p>
          </div>

          {/* SP Performance Rankings */}
          <div className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 shadow-lg">
            <h3 className="text-2xl font-bold text-purple-900 mb-6 flex items-center gap-2">
              <PieChart className="w-6 h-6" />
              Round {roundNum} - Structured Products Performance
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top 3 Performers */}
              <div className="bg-white rounded-lg p-5 border border-emerald-200">
                <h4 className="text-lg font-semibold text-emerald-700 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Top 3 Performers
                </h4>
                <div className="space-y-2 sm:space-y-3">
                  {SP_PERFORMANCE_RANKINGS[`round${roundNum}`].top3.map((sp, idx) => {
                    // Check if client selected this SP
                    let isSelected = false;
                    Object.values(history.portfolioBefore.structuredProducts).forEach(category => {
                      category.forEach(instrument => {
                        if (instrument.ticker === sp.ticker && instrument.allocation > 0) {
                          isSelected = true;
                        }
                      });
                    });

                    // Use ticker as theme (it IS the theme/region in SP_PERFORMANCE_RANKINGS)
                    const theme = sp.ticker;

                    return (
                      <div key={idx} className="flex items-start gap-2 sm:gap-3 p-3 bg-emerald-50 rounded border border-emerald-100">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="font-semibold text-slate-900 text-xs sm:text-sm break-words">{sp.name}</div>
                          {theme && (
                            <div className="text-xs text-slate-500 mt-1">{theme}</div>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-2">
                          <div className="font-bold text-emerald-600 text-lg">{formatPercent(sp.return)}</div>
                          {isSelected ? (
                            <div className="w-5 h-5 bg-emerald-600 rounded flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-5 h-5 border-2 border-slate-400 rounded"></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bottom 3 Performers */}
              <div className="bg-white rounded-lg p-5 border border-amber-200">
                <h4 className="text-lg font-semibold text-amber-700 mb-4 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  Bottom 3 Performers
                </h4>
                
                {/* General Bottom 3 Commentary */}
                {SP_PERFORMANCE_RANKINGS[`round${roundNum}`].bottom3Comment && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="text-sm text-amber-900 italic">
                      💡 {SP_PERFORMANCE_RANKINGS[`round${roundNum}`].bottom3Comment}
                    </div>
                  </div>
                )}
                
                <div className="space-y-2 sm:space-y-3">
                  {SP_PERFORMANCE_RANKINGS[`round${roundNum}`].bottom3.map((sp, idx) => {
                    // Check if client selected this SP
                    let isSelected = false;
                    Object.values(history.portfolioBefore.structuredProducts).forEach(category => {
                      category.forEach(instrument => {
                        if (instrument.ticker === sp.ticker && instrument.allocation > 0) {
                          isSelected = true;
                        }
                      });
                    });

                    // Use ticker as theme (it IS the theme/region in SP_PERFORMANCE_RANKINGS)
                    const theme = sp.ticker;

                    return (
                      <div key={idx} className="p-3 bg-amber-50 rounded border border-amber-100">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="font-semibold text-slate-900 text-xs sm:text-sm break-words">{sp.name}</div>
                            {theme && (
                              <div className="text-xs text-slate-500 mt-1">{theme}</div>
                            )}
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-2">
                            <div className={`font-bold text-lg ${sp.return >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatPercent(sp.return)}
                            </div>
                            {isSelected && (
                              <div className="w-5 h-5 border-2 border-amber-600 rounded-full"></div>
                            )}
                          </div>
                        </div>
                        {/* Show badWarning if user selected it */}
                        {isSelected && sp.badWarning && (
                          <div className="mt-2 text-xs text-red-900 bg-red-100 p-2 rounded font-medium">
                            ⚠️ {sp.badWarning}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Bad SP Warnings */}
          {(() => {
            const badSPsSelected = [];
            // Check both top3 and bottom3 for bad SPs
            [...SP_PERFORMANCE_RANKINGS[`round${roundNum}`].top3, ...SP_PERFORMANCE_RANKINGS[`round${roundNum}`].bottom3].forEach(sp => {
              if (sp.isBad) {
                let isSelected = false;
                Object.values(history.portfolioBefore.structuredProducts).forEach(category => {
                  category.forEach(instrument => {
                    if (instrument.ticker === sp.ticker && instrument.allocation > 0) {
                      isSelected = true;
                    }
                  });
                });
                if (isSelected) {
                  badSPsSelected.push(sp);
                }
              }
            });

            return badSPsSelected.length > 0 ? (
              <div className="mb-8 bg-red-50 border-2 border-red-300 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  ⚠️ Suboptimal SP Selection
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  {badSPsSelected.map((sp, idx) => (
                    <div key={idx} className="bg-white border-2 border-red-200 rounded-lg p-4">
                      <div className="font-semibold text-red-900 mb-2">{sp.name}</div>
                      <div className="text-sm text-red-800">{sp.badWarning}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Concentration Risk Warnings */}
          {(() => {
            const concentrationWarnings = [];
            const totalValue = (() => {
              let total = 0;
              Object.values(history.portfolioBefore.stocks).forEach(stocks => {
                stocks.forEach(s => total += s.allocation);
              });
              Object.values(history.portfolioBefore.bonds).forEach(bonds => {
                bonds.forEach(b => total += b.allocation);
              });
              Object.values(history.portfolioBefore.structuredProducts).forEach(sps => {
                sps.forEach(sp => total += sp.allocation);
              });
              total += history.portfolioBefore.cash;
              return total;
            })();
            
            if (totalValue === 0) return null;
            
            // Check individual instruments > 20%
            Object.entries(history.portfolioBefore.stocks).forEach(([sector, stocks]) => {
              stocks.forEach(stock => {
                if (stock.allocation > 0) {
                  const pct = stock.allocation / totalValue;
                  if (pct > 0.20) {
                    concentrationWarnings.push({
                      type: 'instrument',
                      name: stock.ticker,
                      category: 'Stock',
                      percentage: pct
                    });
                  }
                }
              });
            });
            
            Object.entries(history.portfolioBefore.bonds).forEach(([category, bonds]) => {
              bonds.forEach(bond => {
                if (bond.allocation > 0) {
                  const pct = bond.allocation / totalValue;
                  if (pct > 0.20) {
                    concentrationWarnings.push({
                      type: 'instrument',
                      name: bond.ticker,
                      category: 'Bond',
                      percentage: pct
                    });
                  }
                }
              });
            });
            
            Object.entries(history.portfolioBefore.structuredProducts).forEach(([category, sps]) => {
              sps.forEach(sp => {
                if (sp.allocation > 0) {
                  const pct = sp.allocation / totalValue;
                  if (pct > 0.20) {
                    concentrationWarnings.push({
                      type: 'instrument',
                      name: sp.name,
                      category: 'Structured Product',
                      percentage: pct
                    });
                  }
                }
              });
            });
            
            // Check sector concentration > 60%
            Object.entries(history.portfolioBefore.stocks).forEach(([sector, stocks]) => {
              const sectorTotal = stocks.reduce((sum, s) => sum + s.allocation, 0);
              if (sectorTotal > 0) {
                const pct = sectorTotal / totalValue;
                if (pct > 0.60) {
                  concentrationWarnings.push({
                    type: 'sector',
                    name: sector.charAt(0).toUpperCase() + sector.slice(1),
                    percentage: pct
                  });
                }
              }
            });
            
            return concentrationWarnings.length > 0 ? (
              <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  ⚠️ Concentration Risk Detected
                </h3>
                <div className="space-y-2">
                  {concentrationWarnings.map((warning, idx) => (
                    <div key={idx} className="bg-white border border-amber-200 rounded-lg p-3">
                      {warning.type === 'instrument' ? (
                        <div className="text-sm">
                          <span className="font-semibold text-amber-900">{warning.name}</span>
                          <span className="text-amber-700"> ({warning.category}): </span>
                          <span className="font-bold text-amber-800">{formatPercent(warning.percentage)}</span>
                          <span className="text-amber-600"> - Over-concentrated in single {warning.category.toLowerCase()}</span>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <span className="font-semibold text-amber-900">{warning.name} sector: </span>
                          <span className="font-bold text-amber-800">{formatPercent(warning.percentage)}</span>
                          <span className="text-amber-600"> - Over-concentrated in single sector</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Grouped SP Commentaries */}
          {(() => {
            // Collect all user's SPs with allocations
            const userSPs = [];
            Object.entries(history.portfolioBefore.structuredProducts).forEach(([category, sps]) => {
              sps.forEach(sp => {
                if (sp.allocation > 0) {
                  userSPs.push(sp);
                }
              });
            });
            
            if (userSPs.length === 0) return null;
            
            // Group by commentary
            const commentaryGroups = {};
            
            userSPs.forEach(sp => {
              // In Round 3, skip 6-month SPs — they were bought in Round 2 and their
              // Round 3 return is the realised maturity return, not a single-round outcome
              if (roundNum === 3 && sp.tenor === 6) return;

              // Find commentary for this SP and round
              const spData = STRUCTURED_PRODUCTS_COMMENTARIES[sp.ticker];
              if (spData) {
                const roundData = spData.find(item => item.round === roundNum);
                if (roundData && roundData.commentary) {
                  const commentary = roundData.commentary;
                  if (!commentaryGroups[commentary]) {
                    commentaryGroups[commentary] = [];
                  }
                  commentaryGroups[commentary].push({
                    name: sp.name,
                    ticker: sp.ticker,
                    return: roundData.return
                  });
                }
              }
            });
            
            const groupedEntries = Object.entries(commentaryGroups);
            if (groupedEntries.length === 0) return null;
            
            return (
              <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-indigo-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  💡 Insights on Your Selected Structured Product
                </h3>
                <div className="space-y-4">
                  {groupedEntries.map(([commentary, sps], idx) => (
                    <div key={idx} className="bg-white border border-indigo-200 rounded-lg p-4">
                      <div className="text-sm font-medium text-indigo-900 mb-3">
                        💡 {commentary}:
                      </div>
                      <div className="space-y-1 ml-4">
                        {sps.map((sp, spIdx) => (
                          <div key={spIdx} className="text-sm text-slate-700 flex items-start gap-2">
                            <span className="text-indigo-600">-</span>
                            <span className="flex-1">{sp.name}</span>
                            <span className={`font-bold flex-shrink-0 ${sp.return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              [{formatPercent(sp.return)}]
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8">
            <div className="bg-white border-2 border-slate-200 rounded-lg p-6 shadow-lg">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6 flex items-center gap-2 text-slate-900">
                <DollarSign className="w-6 md:w-7 h-6 md:h-7 text-slate-700" />
                Your Portfolio
              </h2>
              <div className="bg-slate-50 rounded-lg p-6 mb-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600 text-lg">Total Value</span>
                  <span className={`text-3xl md:text-4xl font-bold ${clientReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterTotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 text-lg">Return</span>
                  <span className={`text-xl sm:text-2xl md:text-3xl font-semibold flex items-center gap-2 ${clientReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {clientReturn >= 0 ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
                    {formatPercent(clientReturn)}
                  </span>
                </div>
              </div>

              {/* Market Performance Comparison */}
              {(() => {
                const marketReturn = MARKET_RETURNS[`round${roundNum}`];
                if (clientReturn > marketReturn) {
                  let message = '';
                  if (roundNum === 1) {
                    message = 'Great job! You outperformed the market';
                  } else if (roundNum === 2) {
                    message = 'Excellent! Your portfolio proved resilient through volatile markets';
                  } else if (roundNum === 3) {
                    message = 'Well done! You successfully captured the market rally';
                  }
                  return (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 text-emerald-800">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-semibold">{message}</span>
                      </div>
                      <div className="text-sm text-emerald-700 mt-2">
                        Market Return: {formatPercent(marketReturn)} | Your Return: {formatPercent(clientReturn)}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="space-y-2 sm:space-y-3">
                <div className="text-xs text-slate-400 text-right pr-1 pb-1">% = contribution to total return</div>
                <button
                  onClick={() => setExpandedClientAssets(prev => ({ ...prev, equity: !prev.equity }))}
                  className="w-full"
                >
                  <div className="flex items-center justify-between text-base hover:bg-slate-50 p-3 rounded transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedClientAssets.equity ? <ChevronUp className="w-6 h-6 text-slate-600" /> : <ChevronDown className="w-6 h-6 text-slate-600" />}
                      <span className="text-slate-700 font-semibold text-lg">Equities</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${clientEquityReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterEquity)}</span>
                      <span className={`font-bold text-lg ${clientEquityReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatPercent(clientEquityReturn)}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedClientAssets.equity && (
                  <div className="ml-8 space-y-2 bg-slate-100 p-4 rounded">
                    {Object.keys(history.portfolioBefore.stocks).map(sector => {
                      const beforeValue = history.portfolioBefore.stocks[sector].reduce((sum, s) => sum + s.allocation, 0) * TOTAL_VALUE;
                      const afterValue = history.portfolioAfter.stocks[sector].reduce((sum, s) => sum + s.allocation, 0) * TOTAL_VALUE;
                      const sectorReturn = beforeValue > 0 ? ((afterValue - beforeValue) / beforeTotal) : 0;
                      const isSectorExpanded = expandedClientAssets[`equity-${sector}`];
                      
                      return (
                        <div key={sector}>
                          <button
                            onClick={() => setExpandedClientAssets(prev => ({ ...prev, [`equity-${sector}`]: !prev[`equity-${sector}`] }))}
                            className="w-full flex justify-between text-sm text-slate-700 hover:bg-slate-200 p-2 rounded"
                          >
                            <div className="flex items-center gap-2">
                              {isSectorExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <span className="font-medium">{getSectorLabel(sector)}</span>
                            </div>
                            <div className="flex gap-3">
                              <span className={`font-semibold ${sectorReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterValue)}</span>
                              <span className={sectorReturn >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatPercent(sectorReturn)}
                              </span>
                            </div>
                          </button>
                          {isSectorExpanded && (
                            <div className="ml-6 mt-1 space-y-1 bg-white p-3 rounded">
                              {history.portfolioBefore.stocks[sector].map((stock, idx) => {
                                const beforeVal = stock.allocation * TOTAL_VALUE;
                                const afterVal = history.portfolioAfter.stocks[sector][idx].allocation * TOTAL_VALUE;
                                const stockReturn = beforeVal > 0 ? ((afterVal - beforeVal) / beforeTotal) : 0;
                                const instrumentReturn = STOCK_RETURNS[sector]?.[`round${roundNum}`]?.[stock.ticker] ?? 0;
                                return (
                                  <div key={idx} className="flex justify-between text-xs sm:text-xs text-slate-600 py-1">
                                    <span className="flex-1 min-w-0">
                                      {stock.name} <span className={`font-semibold ${instrumentReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>[{formatPercent(instrumentReturn)}]</span>
                                    </span>
                                    <div className="flex gap-3 flex-shrink-0 ml-2">
                                      <span className={stockReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(afterVal)}</span>
                                      <span className={stockReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                        {formatPercent(stockReturn)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={() => setExpandedClientAssets(prev => ({ ...prev, bonds: !prev.bonds }))}
                  className="w-full"
                >
                  <div className="flex items-center justify-between text-base hover:bg-slate-50 p-3 rounded transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedClientAssets.bonds ? <ChevronUp className="w-6 h-6 text-slate-600" /> : <ChevronDown className="w-6 h-6 text-slate-600" />}
                      <span className="text-slate-700 font-semibold text-lg">Bonds</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${clientBondsReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterBonds)}</span>
                      <span className={`font-bold text-lg ${clientBondsReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatPercent(clientBondsReturn)}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedClientAssets.bonds && (
                  <div className="ml-8 space-y-2 bg-slate-100 p-4 rounded">
                    {Object.keys(history.portfolioBefore.bonds).map(category => {
                      const beforeValue = history.portfolioBefore.bonds[category].reduce((sum, b) => sum + b.allocation, 0) * TOTAL_VALUE;
                      const afterValue = history.portfolioAfter.bonds[category].reduce((sum, b) => sum + b.allocation, 0) * TOTAL_VALUE;
                      const categoryReturn = beforeValue > 0 ? ((afterValue - beforeValue) / beforeTotal) : 0;
                      const isCategoryExpanded = expandedClientAssets[`bonds-${category}`];
                      
                      return (
                        <div key={category}>
                          <button
                            onClick={() => setExpandedClientAssets(prev => ({ ...prev, [`bonds-${category}`]: !prev[`bonds-${category}`] }))}
                            className="w-full flex justify-between text-sm text-slate-700 hover:bg-slate-200 p-2 rounded"
                          >
                            <div className="flex items-center gap-2">
                              {isCategoryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <span className="font-medium">{getSectorLabel(category)}</span>
                            </div>
                            <div className="flex gap-3">
                              <span className={`font-semibold ${categoryReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterValue)}</span>
                              <span className={categoryReturn >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatPercent(categoryReturn)}
                              </span>
                            </div>
                          </button>
                          {isCategoryExpanded && (
                            <div className="ml-6 mt-1 space-y-1 bg-white p-3 rounded">
                              {history.portfolioBefore.bonds[category].map((bond, idx) => {
                                const beforeVal = bond.allocation * TOTAL_VALUE;
                                const afterVal = history.portfolioAfter.bonds[category][idx].allocation * TOTAL_VALUE;
                                const bondReturn = beforeVal > 0 ? ((afterVal - beforeVal) / beforeTotal) : 0;
                                const instrumentReturn = BOND_RETURNS[category]?.[`round${roundNum}`]?.[bond.ticker] ?? 0;
                                return (
                                  <div key={idx} className="flex justify-between text-xs sm:text-xs text-slate-600 py-1">
                                    <span className="flex-1 min-w-0">
                                      {bond.name} <span className={`font-semibold ${instrumentReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>[{formatPercent(instrumentReturn)}]</span>
                                    </span>
                                    <div className="flex gap-3 flex-shrink-0 ml-2">
                                      <span className={bondReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(afterVal)}</span>
                                      <span className={bondReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                        {formatPercent(bondReturn)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {(() => {
                  // Helper: was this SP shown on the input screen for this round?
                  // Use STRUCTURED_PRODUCTS as ground truth for the display field.
                  // Also always show SPs the participant actually allocated to (e.g. carried over).
                  const wasOnInputScreen = (category, sp) => {
                    const master = STRUCTURED_PRODUCTS[category]?.find(s => s.ticker === sp.ticker);
                    const display = master?.display ?? sp.display ?? '';
                    return display.split(',').map(s => s.trim()).includes(String(roundNum))
                      || sp.allocation > 0;
                  };

                  // Only render the section if at least one SP was on the input screen
                  const hasVisibleSPs = Object.entries(history.portfolioBefore.structuredProducts)
                    .some(([cat, sps]) => sps.some(sp => wasOnInputScreen(cat, sp)));

                  if (!hasVisibleSPs) return null;

                  return <>
                <button
                  onClick={() => setExpandedClientAssets(prev => ({ ...prev, sp: !prev.sp }))}
                  className="w-full"
                >
                  <div className="flex items-center justify-between text-base hover:bg-slate-50 p-3 rounded transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedClientAssets.sp ? <ChevronUp className="w-6 h-6 text-slate-600" /> : <ChevronDown className="w-6 h-6 text-slate-600" />}
                      <span className="text-slate-700 font-semibold text-lg">Structured Products</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${clientSPReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterSP)}</span>
                      <span className={`font-bold text-lg ${clientSPReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatPercent(clientSPReturn)}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedClientAssets.sp && (
                  <div className="ml-8 space-y-2 bg-slate-100 p-4 rounded">
                    {Object.keys(history.portfolioBefore.structuredProducts).map(category => {
                      // Only show SPs that were on the input screen (or were allocated to)
                      const visibleSPs = history.portfolioBefore.structuredProducts[category]
                        .map((sp, idx) => ({ sp, idx }))
                        .filter(({ sp }) => wasOnInputScreen(category, sp));

                      if (visibleSPs.length === 0) return null;

                      const beforeValue = visibleSPs.reduce((sum, { sp }) => sum + sp.allocation, 0) * TOTAL_VALUE;
                      const afterValue = visibleSPs.reduce((sum, { idx }) =>
                        sum + history.portfolioAfter.structuredProducts[category][idx].allocation, 0) * TOTAL_VALUE;
                      const categoryReturn = beforeValue > 0 ? ((afterValue - beforeValue) / beforeTotal) : 0;
                      const isCategoryExpanded = expandedClientAssets[`sp-${category}`];
                      
                      return (
                        <div key={category}>
                          <button
                            onClick={() => setExpandedClientAssets(prev => ({ ...prev, [`sp-${category}`]: !prev[`sp-${category}`] }))}
                            className="w-full flex justify-between text-sm text-slate-700 hover:bg-slate-200 p-2 rounded"
                          >
                            <div className="flex items-center gap-2">
                              {isCategoryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <span className="font-medium">{getSectorLabel(category)}</span>
                            </div>
                            <div className="flex gap-3">
                              <span className={`font-semibold ${categoryReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterValue)}</span>
                              <span className={categoryReturn >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatPercent(categoryReturn)}
                              </span>
                            </div>
                          </button>
                          {isCategoryExpanded && (
                            <div className="ml-6 mt-1 space-y-1 bg-white p-3 rounded">
                              {visibleSPs.map(({ sp, idx }) => {
                                const beforeVal = sp.allocation * TOTAL_VALUE;
                                const afterVal = history.portfolioAfter.structuredProducts[category][idx].allocation * TOTAL_VALUE;
                                const spReturn = beforeVal > 0 ? ((afterVal - beforeVal) / beforeTotal) : 0;
                                const instrumentReturn = SP_RETURNS[`round${roundNum}`]?.[sp.ticker] ?? 0;
                                return (
                                  <div key={idx} className="flex justify-between text-xs sm:text-xs text-slate-600 py-1">
                                    <span className="flex-1 min-w-0">
                                      {sp.name} <span className={`font-semibold ${instrumentReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>[{formatPercent(instrumentReturn)}]</span>
                                    </span>
                                    <div className="flex gap-3 flex-shrink-0 ml-2">
                                      <span className={spReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(afterVal)}</span>
                                      <span className={spReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                        {formatPercent(spReturn)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                  </>;
                })()}

                <div className="flex items-center justify-between text-base p-3">
                  <span className="text-slate-700 font-semibold text-lg">Cash</span>
                  <span className="font-bold text-slate-700 text-lg">{formatCurrency(afterCash)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-emerald-200 rounded-lg p-6 shadow-lg">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-6 flex items-center gap-2 text-slate-900">
                <AlertCircle className="w-6 md:w-7 h-6 md:h-7 text-slate-600" />
                Proposed Portfolio
              </h2>
              <div className="bg-emerald-50 rounded-lg p-6 mb-6 border border-emerald-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600 text-lg">Total Value</span>
                  <span className={`text-3xl md:text-4xl font-bold ${optimalReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(optimalAfterTotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 text-lg">Return</span>
                  <span className={`text-xl sm:text-2xl md:text-3xl font-semibold flex items-center gap-2 ${optimalReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {optimalReturn >= 0 ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
                    {formatPercent(optimalReturn)}
                  </span>
                </div>
              </div>

              {/* Bad SP Warnings */}
              {history.incorrectSPs && history.incorrectSPs.length > 0 && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6">
                  <div className="text-sm text-red-900 font-semibold mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    ⚠️ Suboptimal SP Selection
                  </div>
                  {history.incorrectSPs.map((badSP, idx) => (
                    <div key={idx} className="bg-white border-2 border-red-200 rounded-lg p-3 mb-2">
                      <div className="font-semibold text-red-900 text-sm mb-1">{badSP.name} ({badSP.ticker})</div>
                      <div className="text-xs text-red-800">{badSP.warning}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recommended Switches */}
              {history.switchDetails && history.switchDetails.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                  <div className="text-sm text-emerald-700 font-semibold mb-3">
                    💡 Recommended Portfolio Improvements ({history.switchDetails.length} switches)
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      // Separate hedge additions from switches
                      const hedgeAdded = history.switchDetails.find(sw => sw.type === 'hedge_added');
                      const regularSwitches = history.switchDetails.filter(sw => sw.type !== 'hedge_added');
                      
                      // Sort switches by return improvement (largest improvement first)
                      const sortedSwitches = [...regularSwitches].sort((a, b) => {
                        const aFromReturn = a.stockReturn !== undefined ? a.stockReturn : a.bondReturn;
                        const bFromReturn = b.stockReturn !== undefined ? b.stockReturn : b.bondReturn;
                        const aImprovement = a.spReturn - aFromReturn;
                        const bImprovement = b.spReturn - bFromReturn;
                        return bImprovement - aImprovement; // Descending order
                      });
                      
                      // Combine: hedge first (if exists), then top 5 switches
                      const toDisplay = [];
                      if (hedgeAdded) toDisplay.push(hedgeAdded);
                      toDisplay.push(...sortedSwitches.slice(0, 5));
                      
                      return toDisplay.map((sw, idx) => {
                        // Display hedge_added
                        if (sw.type === 'hedge_added') {
                          return (
                            <div key={idx} className="bg-green-50 rounded p-2 text-xs border border-green-200">
                              <div className="font-medium text-green-900">Hedging Added</div>
                              <div className="text-green-800 mt-1">
                                Protected {formatCurrency(sw.totalProtected * TOTAL_VALUE)} with {formatCurrency(sw.amount * TOTAL_VALUE)} hedge ({formatPercent(sw.premium)} premium)
                              </div>
                            </div>
                          );
                        }
                        
                        const fromReturn = sw.stockReturn !== undefined ? sw.stockReturn : sw.bondReturn;
                        const isStockSwitch = sw.stockReturn !== undefined;
                        const fromLabel = isStockSwitch ? 'Stock' : 'Bond';
                        
                        return (
                          <div key={idx} className="bg-white rounded p-2 text-xs">
                            <div className="font-medium text-slate-900">{sw.from} → {sw.toName}</div>
                            <div className="text-slate-600 mt-1">
                              Improved outcome: {' '}
                              <span className={`font-semibold ${fromReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatPercent(fromReturn)}
                              </span>
                              {' → '}
                              <span className={`font-semibold ${sw.spReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatPercent(sw.spReturn)}
                              </span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                    {history.switchDetails.filter(sw => sw.type !== 'hedge_added').length > 5 && (
                      <div className="text-xs text-emerald-600 italic">
                        ...and {history.switchDetails.filter(sw => sw.type !== 'hedge_added').length - 5} more switches
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Recommendations */}
              {(() => {
                const additionalRecs = generateAdditionalRecommendations(
                  roundNum, 
                  history.optimal, 
                  history.portfolioAfter, 
                  roundHistory
                );
                
                if (additionalRecs.length === 0) return null;
                
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="text-sm text-blue-700 font-semibold mb-3">
                      💡 Deploy cash to CRAN/Term Note/MRNs for enhanced return
                    </div>
                    <div className="space-y-2">
                      {additionalRecs.map((rec, idx) => {
                        // HIGH CASH: Add recommendations
                        if (rec.type === 'high_cash') {
                          return (
                            <div key={idx} className="bg-white rounded p-3 text-xs">
                              <div className="font-medium text-slate-900 mb-1">
                                Added {rec.name}
                              </div>
                              <div className="text-slate-600">
                                <span className="font-semibold">{formatCurrency(rec.amount)}</span> - {rec.reason}
                              </div>
                            </div>
                          );
                        }
                        
                        // RECOVERY NEEDED (Round 3)
                        if (rec.type === 'recovery_needed') {
                          return (
                            <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs">
                              <div className="font-medium text-yellow-900 mb-1">
                                📊 Recovery Analysis
                              </div>
                              <div className="text-yellow-800">{rec.message}</div>
                            </div>
                          );
                        }
                        
                        return null;
                      })}
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-2 sm:space-y-3">
                <div className="text-xs text-slate-400 text-right pr-1 pb-1">% = contribution to total return</div>
                <button
                  onClick={() => setExpandedOptimalAssets(prev => ({ ...prev, equity: !prev.equity }))}
                  className="w-full"
                >
                  <div className="flex items-center justify-between text-base hover:bg-emerald-50 p-3 rounded transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedOptimalAssets.equity ? <ChevronUp className="w-6 h-6 text-slate-600" /> : <ChevronDown className="w-6 h-6 text-slate-600" />}
                      <span className="text-slate-700 font-semibold text-lg">Equities</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${optimalEquityReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(optimalAfterEquity)}</span>
                      <span className={`font-bold text-lg ${optimalEquityReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatPercent(optimalEquityReturn)}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedOptimalAssets.equity && (
                  <div className="ml-8 space-y-2 bg-emerald-50 p-4 rounded">
                    {Object.keys(history.optimal.stocks).map(sector => {
                      const beforeValue = history.optimal.stocks[sector]?.reduce((sum, s) => sum + s.allocation, 0) * TOTAL_VALUE || 0;
                      if (beforeValue === 0) return null;
                      const afterValue = optimalAfter.stocks[sector]?.reduce((sum, s) => sum + s.allocation, 0) * TOTAL_VALUE || 0;
                      const actualSectorReturn = beforeValue > 0 ? ((afterValue - beforeValue) / optimalBeforeTotal) : 0;
                      const isSectorExpanded = expandedOptimalAssets[`equity-${sector}`];
                      
                      return (
                        <div key={sector}>
                          <button
                            onClick={() => setExpandedOptimalAssets(prev => ({ ...prev, [`equity-${sector}`]: !prev[`equity-${sector}`] }))}
                            className="w-full flex justify-between text-sm text-slate-700 hover:bg-slate-200 p-2 rounded"
                          >
                            <div className="flex items-center gap-2">
                              {isSectorExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <span className="font-medium">{getSectorLabel(sector)}</span>
                            </div>
                            <div className="flex gap-3">
                              <span className={`font-semibold ${actualSectorReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterValue)}</span>
                              <span className={actualSectorReturn >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatPercent(actualSectorReturn)}
                              </span>
                            </div>
                          </button>
                          {isSectorExpanded && history.optimal.stocks[sector] && (
                            <div className="ml-6 mt-1 space-y-1 bg-white p-3 rounded">
                              {history.optimal.stocks[sector].map((stock, idx) => {
                                if (stock.allocation === 0) return null;
                                const beforeVal = stock.allocation * TOTAL_VALUE;
                                const afterVal = optimalAfter.stocks[sector][idx].allocation * TOTAL_VALUE;
                                const stockReturn = beforeVal > 0 ? ((afterVal - beforeVal) / optimalBeforeTotal) : 0;
                                const instrumentReturn = STOCK_RETURNS[sector]?.[`round${roundNum}`]?.[stock.ticker] ?? 0;
                                return (
                                  <div key={idx} className="flex justify-between text-xs text-slate-600 py-1">
                                    <span className="flex-1 min-w-0">
                                      {stock.name} <span className={`font-semibold ${instrumentReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>[{formatPercent(instrumentReturn)}]</span>
                                    </span>
                                    <div className="flex gap-3 flex-shrink-0 ml-2">
                                      <span className={stockReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(afterVal)}</span>
                                      <span className={stockReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                        {formatPercent(stockReturn)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={() => setExpandedOptimalAssets(prev => ({ ...prev, bonds: !prev.bonds }))}
                  className="w-full"
                >
                  <div className="flex items-center justify-between text-base hover:bg-emerald-50 p-3 rounded transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedOptimalAssets.bonds ? <ChevronUp className="w-6 h-6 text-slate-600" /> : <ChevronDown className="w-6 h-6 text-slate-600" />}
                      <span className="text-slate-700 font-semibold text-lg">Bonds</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${optimalBondsReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(optimalAfterBonds)}</span>
                      <span className={`font-bold text-lg ${optimalBondsReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatPercent(optimalBondsReturn)}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedOptimalAssets.bonds && (
                  <div className="ml-8 space-y-2 bg-emerald-50 p-4 rounded">
                    {Object.keys(history.optimal.bonds).map(category => {
                      const beforeValue = history.optimal.bonds[category]?.reduce((sum, b) => sum + b.allocation, 0) * TOTAL_VALUE || 0;
                      if (beforeValue === 0) return null;
                      const afterValue = optimalAfter.bonds[category]?.reduce((sum, b) => sum + b.allocation, 0) * TOTAL_VALUE || 0;
                      const actualCategoryReturn = beforeValue > 0 ? ((afterValue - beforeValue) / optimalBeforeTotal) : 0;
                      const isCategoryExpanded = expandedOptimalAssets[`bonds-${category}`];
                      
                      return (
                        <div key={category}>
                          <button
                            onClick={() => setExpandedOptimalAssets(prev => ({ ...prev, [`bonds-${category}`]: !prev[`bonds-${category}`] }))}
                            className="w-full flex justify-between text-sm text-slate-700 hover:bg-slate-200 p-2 rounded"
                          >
                            <div className="flex items-center gap-2">
                              {isCategoryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <span className="font-medium">{getSectorLabel(category)}</span>
                            </div>
                            <div className="flex gap-3">
                              <span className={`font-semibold ${actualCategoryReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterValue)}</span>
                              <span className={actualCategoryReturn >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatPercent(actualCategoryReturn)}
                              </span>
                            </div>
                          </button>
                          {isCategoryExpanded && history.optimal.bonds[category] && (
                            <div className="ml-6 mt-1 space-y-1 bg-white p-3 rounded">
                              {history.optimal.bonds[category].map((bond, idx) => {
                                if (bond.allocation === 0) return null;
                                const beforeVal = bond.allocation * TOTAL_VALUE;
                                const afterVal = optimalAfter.bonds[category][idx].allocation * TOTAL_VALUE;
                                const bondReturn = beforeVal > 0 ? ((afterVal - beforeVal) / optimalBeforeTotal) : 0;
                                const instrumentReturn = BOND_RETURNS[category]?.[`round${roundNum}`]?.[bond.ticker] ?? 0;
                                return (
                                  <div key={idx} className="flex justify-between text-xs text-slate-600 py-1">
                                    <span className="flex-1 min-w-0">
                                      {bond.name} <span className={`font-semibold ${instrumentReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>[{formatPercent(instrumentReturn)}]</span>
                                    </span>
                                    <div className="flex gap-3 flex-shrink-0 ml-2">
                                      <span className={bondReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(afterVal)}</span>
                                      <span className={bondReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                        {formatPercent(bondReturn)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={() => setExpandedOptimalAssets(prev => ({ ...prev, sp: !prev.sp }))}
                  className="w-full"
                >
                  <div className="flex items-center justify-between text-base hover:bg-emerald-50 p-3 rounded transition-colors">
                    <div className="flex items-center gap-2">
                      {expandedOptimalAssets.sp ? <ChevronUp className="w-6 h-6 text-slate-600" /> : <ChevronDown className="w-6 h-6 text-slate-600" />}
                      <span className="text-slate-700 font-semibold text-lg">Structured Products</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${optimalSPReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(optimalAfterSP)}</span>
                      <span className={`font-bold text-lg ${optimalSPReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatPercent(optimalSPReturn)}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedOptimalAssets.sp && (
                  <div className="ml-8 space-y-2 bg-emerald-50 p-4 rounded">
                    {Object.keys(history.optimal.structuredProducts).map(category => {
                      const beforeValue = history.optimal.structuredProducts[category]?.reduce((sum, sp) => sum + sp.allocation, 0) * TOTAL_VALUE || 0;
                      if (beforeValue === 0) return null;
                      const afterValue = optimalAfter.structuredProducts[category]?.reduce((sum, sp) => sum + sp.allocation, 0) * TOTAL_VALUE || 0;
                      const actualCategoryReturn = beforeValue > 0 ? ((afterValue - beforeValue) / optimalBeforeTotal) : 0;
                      const isCategoryExpanded = expandedOptimalAssets[`sp-${category}`];
                      
                      return (
                        <div key={category}>
                          <button
                            onClick={() => setExpandedOptimalAssets(prev => ({ ...prev, [`sp-${category}`]: !prev[`sp-${category}`] }))}
                            className="w-full flex justify-between text-sm text-slate-700 hover:bg-slate-200 p-2 rounded"
                          >
                            <div className="flex items-center gap-2">
                              {isCategoryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              <span className="font-medium">{getSectorLabel(category)}</span>
                            </div>
                            <div className="flex gap-3">
                              <span className={`font-semibold ${actualCategoryReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(afterValue)}</span>
                              <span className={actualCategoryReturn >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatPercent(actualCategoryReturn)}
                              </span>
                            </div>
                          </button>
                          {isCategoryExpanded && history.optimal.structuredProducts[category] && (
                            <div className="ml-6 mt-1 space-y-1 bg-white p-3 rounded">
                              {history.optimal.structuredProducts[category].map((sp, idx) => {
                                if (sp.allocation === 0) return null;
                                const beforeVal = sp.allocation * TOTAL_VALUE;
                                const afterVal = optimalAfter.structuredProducts[category][idx].allocation * TOTAL_VALUE;
                                const spReturn = beforeVal > 0 ? ((afterVal - beforeVal) / optimalBeforeTotal) : 0;
                                const instrumentReturn = SP_RETURNS[`round${roundNum}`]?.[sp.ticker] ?? 0;
                                return (
                                  <div key={idx} className="flex justify-between text-xs text-slate-600 py-1">
                                    <span className="flex-1 min-w-0">
                                      {sp.name} <span className={`font-semibold ${instrumentReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>[{formatPercent(instrumentReturn)}]</span>
                                    </span>
                                    <div className="flex gap-3 flex-shrink-0 ml-2">
                                      <span className={spReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(afterVal)}</span>
                                      <span className={spReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                        {formatPercent(spReturn)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between text-base p-3">
                  <span className="text-slate-700 font-semibold text-lg">Cash</span>
                  <span className="font-bold text-slate-700 text-lg">{formatCurrency(optimalAfterCash)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-6 md:p-8 mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl font-semibold mb-4 text-purple-900">{commentary.title}</h3>
            <ul className="text-slate-800 leading-relaxed text-sm md:text-base space-y-2 list-disc pl-5">
              {commentary.content.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center">
            {currentRound < 3 ? (
              <div className="text-center">
                {!pendingAction ? (
                  <button
                    onClick={() => requestPasswordFor(currentRound === 1 ? 'round2' : 'round3')}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-6 sm:px-8 md:px-12 py-3 md:py-4 w-full sm:w-auto rounded-lg text-base md:text-lg font-semibold shadow-lg transition-all hover:shadow-xl hover:scale-105"
                  >
                    Proceed to Round {currentRound + 1}
                  </button>
                ) : (
                  <div className="bg-white border-2 border-slate-300 rounded-lg p-6 shadow-lg">
                    <p className="text-slate-700 mb-4 font-medium">Enter password to proceed to Round {currentRound + 1}:</p>
                    <div className="flex gap-3 justify-center items-start">
                      <div>
                        <input
                          type="password"
                          value={passwordInput}
                          onChange={(e) => setPasswordInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                          className="border-2 border-slate-300 rounded-lg px-4 py-2 text-center text-lg focus:outline-none focus:border-slate-500"
                          placeholder="****"
                          autoFocus
                        />
                        {passwordError && (
                          <p className="text-red-600 text-sm mt-2">{passwordError}</p>
                        )}
                      </div>
                      <button
                        onClick={handlePasswordSubmit}
                        className="bg-slate-900 hover:bg-slate-800 text-white px-4 sm:px-6 py-3 rounded-lg text-sm sm:text-base font-semibold transition-all"
                      >
                        Submit
                      </button>
                      <button
                        onClick={() => {
                          setPendingAction(null);
                          setPasswordInput('');
                          setPasswordError('');
                        }}
                        className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-2 rounded-lg font-semibold transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Simulation Complete!</div>
                <p className="text-slate-700 mb-6 text-sm md:text-base">You've experienced how different structured products perform across market cycles.</p>
                {!pendingAction ? (
                  <button
                    onClick={() => requestPasswordFor('restart')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg font-semibold transition-all hover:scale-105 text-sm md:text-base"
                  >
                    Start New Simulation
                  </button>
                ) : (
                  <div className="bg-white border-2 border-purple-300 rounded-lg p-6 shadow-lg inline-block">
                    <p className="text-slate-700 mb-4 font-medium">Enter password to start new simulation:</p>
                    <div className="flex gap-3 justify-center items-start">
                      <div>
                        <input
                          type="password"
                          value={passwordInput}
                          onChange={(e) => setPasswordInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                          className="border-2 border-purple-300 rounded-lg px-4 py-2 text-center text-lg focus:outline-none focus:border-purple-500"
                          placeholder="****"
                          autoFocus
                        />
                        {passwordError && (
                          <p className="text-red-600 text-sm mt-2">{passwordError}</p>
                        )}
                      </div>
                      <button
                        onClick={handlePasswordSubmit}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-all"
                      >
                        Submit
                      </button>
                      <button
                        onClick={() => {
                          setPendingAction(null);
                          setPasswordInput('');
                          setPasswordError('');
                        }}
                        className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-2 rounded-lg font-semibold transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </>
    );
  }

  return null;
};

export default InvestmentGame;
